import React, { Suspense, useMemo, useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { OrbitControls, Environment, Sky, ContactShadows, Html } from '@react-three/drei'
import { Physics, RigidBody } from '@react-three/rapier'
import { Play, Flag, CircleDot } from 'lucide-react'
import * as THREE from 'three'
import { useTranslation } from '../hooks/use-i18n'
import golfPlayService from '../services/golfPlayService'
import golfTrainingService from '../services/golfTrainingService'
import { useGolfWindStore } from '../store/golfWindStore'
import { GolfPlayerRound } from '../types/golfPlay'

const Tree: React.FC<{ x: number; z: number; scale?: number }> = ({ x, z, scale = 1 }) => (
  <group position={[x, 0, z]} scale={scale}>
    <mesh position={[0, 1.2, 0]}>
      <cylinderGeometry args={[0.16, 0.22, 2.3, 8]} />
      <meshStandardMaterial color="#5a3b22" roughness={0.9} />
    </mesh>
    <mesh position={[0, 2.8, 0]}>
      <sphereGeometry args={[1.1, 14, 14]} />
      <meshStandardMaterial color="#2f7d32" roughness={0.95} />
    </mesh>
    <mesh position={[0.5, 2.6, -0.3]}>
      <sphereGeometry args={[0.8, 12, 12]} />
      <meshStandardMaterial color="#2b6f30" roughness={0.95} />
    </mesh>
  </group>
)

const Hill: React.FC<{ x: number; z: number; sx?: number; sy?: number; sz?: number; color?: string }> = ({
  x,
  z,
  sx = 1,
  sy = 1,
  sz = 1,
  color = '#2c7a3e',
}) => (
  <mesh position={[x, -0.7, z]} scale={[sx, sy, sz]} receiveShadow castShadow>
    <sphereGeometry args={[2.8, 28, 20]} />
    <meshStandardMaterial color={color} roughness={0.96} metalness={0.02} />
  </mesh>
)

const Mountain: React.FC<{ x: number; z: number; height?: number }> = ({ x, z, height = 9 }) => (
  <mesh position={[x, height * 0.35 - 2.3, z]} castShadow receiveShadow>
    <coneGeometry args={[5.5, height, 12]} />
    <meshStandardMaterial color="#455a45" roughness={1} metalness={0} />
  </mesh>
)

const ClubThumbnail: React.FC<{ clubId: string; clubLabel: string }> = ({ clubId, clubLabel }) => {
  const isWedge = clubId === 'aw' || clubId === 'sw' || clubId === 'lw'
  const isPutter = clubId === 'putter'
  const isWood = clubId === 'driver' || clubId === 'wood3'
  const shaftColor = '#dbe7f2'
  const headColor = isPutter ? '#bfc7cf' : isWedge ? '#c7d0d8' : isWood ? '#3f3b36' : '#8e96a0'
  const gripColor = '#2a2f36'

  return (
    <div className="h-12 w-full rounded-md border border-white/10 bg-gradient-to-br from-slate-700/45 to-slate-900/45 mb-1 overflow-hidden">
      <svg viewBox="0 0 120 52" className="h-full w-full">
        <rect x="0" y="44" width="120" height="8" fill="#315f38" opacity="0.65" />
        <line x1="24" y1="10" x2="78" y2="38" stroke={shaftColor} strokeWidth="2.2" strokeLinecap="round" />
        <rect x="18" y="7" width="10" height="4" rx="1.2" fill={gripColor} />
        {isPutter ? (
          <rect x="74" y="34" width="13" height="5" rx="1.5" fill={headColor} />
        ) : isWedge ? (
          <path d="M74 32 L86 34 L83 40 L72 38 Z" fill={headColor} />
        ) : isWood ? (
          <ellipse cx="81" cy="36.5" rx="8.5" ry="5.2" fill={headColor} />
        ) : (
          <path d="M75 31 L88 35 L84 41 L73 38 Z" fill={headColor} />
        )}
        <text x="6" y="14" fontSize="6.5" fill="#d6e4f0" opacity="0.9">{clubLabel}</text>
      </svg>
    </div>
  )
}

const Rock: React.FC<{ x: number; z: number; s?: number }> = ({ x, z, s = 1 }) => (
  <mesh position={[x, -0.15, z]} scale={[s * 1.2, s * 0.8, s]} castShadow receiveShadow>
    <dodecahedronGeometry args={[0.5, 0]} />
    <meshStandardMaterial color="#78766f" roughness={0.98} metalness={0.02} />
  </mesh>
)

const GrassClump: React.FC<{ x: number; z: number; s?: number; windStrength: number }> = ({ x, z, s = 1, windStrength }) => {
  const groupRef = useRef<any>(null)
  useFrame(({ clock }) => {
    if (!groupRef.current) return
    const t = clock.getElapsedTime()
    groupRef.current.rotation.z = Math.sin(t * 1.8 + x * 0.1 + z * 0.15) * (0.03 + windStrength * 0.0008)
  })
  return (
    <group ref={groupRef} position={[x, -0.31, z]} scale={s}>
      <mesh rotation={[0.1, Math.PI / 4, 0]} castShadow>
        <planeGeometry args={[0.14, 0.72]} />
        <meshStandardMaterial color="#3f8f3f" side={2} roughness={0.95} />
      </mesh>
      <mesh rotation={[0.05, -Math.PI / 5, 0]} castShadow>
        <planeGeometry args={[0.14, 0.64]} />
        <meshStandardMaterial color="#468f44" side={2} roughness={0.95} />
      </mesh>
      <mesh rotation={[0.08, Math.PI / 2.2, 0]} castShadow>
        <planeGeometry args={[0.12, 0.68]} />
        <meshStandardMaterial color="#3b843a" side={2} roughness={0.95} />
      </mesh>
    </group>
  )
}

const CloudShadowLayer: React.FC = () => {
  const layerRef = useRef<any>(null)
  useFrame(({ clock }) => {
    if (!layerRef.current) return
    const t = clock.getElapsedTime()
    layerRef.current.position.x = ((t * 0.45) % 36) - 18
  })
  return (
    <group ref={layerRef} position={[-18, -0.325, -4]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[20, 14]} />
        <meshStandardMaterial color="#2d4a37" transparent opacity={0.09} roughness={1} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0.12, 0]} position={[9, 0.002, -5]} receiveShadow>
        <planeGeometry args={[14, 10]} />
        <meshStandardMaterial color="#2d4a37" transparent opacity={0.07} roughness={1} />
      </mesh>
    </group>
  )
}

const WaterHazard: React.FC<{ isSunset: boolean }> = ({ isSunset }) => {
  const waterRef = useRef<any>(null)
  useFrame(({ clock }) => {
    if (!waterRef.current) return
    const t = clock.getElapsedTime()
    waterRef.current.roughness = 0.08 + Math.sin(t * 1.2) * 0.015
    waterRef.current.clearcoat = 0.6 + Math.sin(t * 0.8) * 0.06
  })
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-10, -0.36, -4]} scale={[4.8, 2.1, 1]} receiveShadow>
      <circleGeometry args={[1, 36]} />
      <meshPhysicalMaterial
        ref={waterRef}
        color={isSunset ? '#4576c7' : '#2f7fe0'}
        roughness={0.08}
        metalness={0.05}
        transmission={0.03}
        clearcoat={0.6}
      />
    </mesh>
  )
}

const WindFlag: React.FC<{ windStrength: number }> = ({ windStrength }) => {
  const clothRef = useRef<any>(null)
  useFrame(({ clock }) => {
    if (!clothRef.current) return
    const t = clock.getElapsedTime()
    const wave = Math.sin(t * (2 + windStrength * 0.12)) * 0.18
    clothRef.current.rotation.y = -0.25 - windStrength * 0.01 + wave * 0.4
    clothRef.current.rotation.x = wave * 0.12
  })

  return (
    <group position={[0.22, 1.25, -10]}>
      <mesh ref={clothRef}>
        <planeGeometry args={[0.6, 0.35, 6, 4]} />
        <meshStandardMaterial color="#22c55e" side={2} roughness={0.7} metalness={0.05} />
      </mesh>
    </group>
  )
}

const TrainingBallPhysics: React.FC<{
  shotRequest: ShotRequest | null
  windStrength: number
  windAngle: number
  onLanding: (landing: { x: number; z: number }, request: ShotRequest) => void
}> = ({ shotRequest, windStrength, windAngle, onLanding }) => {
  const bodyRef = useRef<any>(null)
  const activeRef = useRef(false)
  const landedRef = useRef(false)
  const shotStartAtRef = useRef(0)
  const teePos = useMemo(() => ({ x: -2.5, y: -0.08, z: 6 }), [])

  useEffect(() => {
    if (!bodyRef.current || !shotRequest) return
    bodyRef.current.setTranslation({ x: teePos.x, y: teePos.y, z: teePos.z }, true)
    bodyRef.current.setLinvel({ x: 0, y: 0, z: 0 }, true)
    bodyRef.current.setAngvel({ x: 0, y: 0, z: 0 }, true)
    bodyRef.current.wakeUp()
    bodyRef.current.applyImpulse(shotRequest.impulse, true)
    activeRef.current = true
    landedRef.current = false
    shotStartAtRef.current = performance.now()
  }, [shotRequest, teePos])

  useFrame(() => {
    if (!bodyRef.current) return
    if (activeRef.current) {
      const linvel = bodyRef.current.linvel()
      const speed = Math.sqrt(linvel.x * linvel.x + linvel.y * linvel.y + linvel.z * linvel.z)
      const t = bodyRef.current.translation()
      const elapsed = performance.now() - shotStartAtRef.current

      // Apply wind while the ball is in meaningful motion; let it settle naturally near rest.
      if (speed > 0.35 || t.y > 0.25) {
        const windRad = (windAngle * Math.PI) / 180
        const windForce = windStrength * 0.008
        bodyRef.current.addForce(
          { x: Math.cos(windRad) * windForce, y: 0, z: Math.sin(windRad) * windForce },
          true,
        )
      }

      const hasSettled = elapsed > 700 && speed < 0.18 && t.y < 0.2
      const timedOut = elapsed > 6500
      if (!landedRef.current && (hasSettled || timedOut) && shotRequest) {
        landedRef.current = true
        activeRef.current = false
        onLanding({ x: t.x, z: t.z }, shotRequest)
        window.setTimeout(() => {
          if (!bodyRef.current) return
          bodyRef.current.setTranslation({ x: teePos.x, y: teePos.y, z: teePos.z }, true)
          bodyRef.current.setLinvel({ x: 0, y: 0, z: 0 }, true)
          bodyRef.current.setAngvel({ x: 0, y: 0, z: 0 }, true)
        }, 1000)
      }
    }
  })

  return (
    <RigidBody ref={bodyRef} colliders="ball" restitution={0.14} friction={0.86} linearDamping={0.4} angularDamping={1.25}>
      <mesh castShadow>
        <sphereGeometry args={[0.12, 20, 20]} />
        <meshStandardMaterial color="#f8fafc" metalness={0.1} roughness={0.2} />
      </mesh>
    </RigidBody>
  )
}

const CameraFollowRig: React.FC<{
  isAnimating: boolean
  animProgress: number
  achieved: number
  lateral: number
}> = ({ isAnimating, animProgress, achieved, lateral }) => {
  const { camera } = useThree()
  useFrame(() => {
    const defaultPos = new THREE.Vector3(0, 6.2, 12.5)
    const eased = THREE.MathUtils.smoothstep(animProgress, 0, 1)
    const flightPos = new THREE.Vector3(
      0 + (lateral / 100) * 2.2 * eased,
      6.35 - eased * 0.12,
      12.5 - Math.min(6.5, (achieved / 260) * 6.5) * eased,
    )
    const targetPos = isAnimating ? flightPos : defaultPos
    camera.position.lerp(targetPos, isAnimating ? 0.12 : 0.06)
    // Keep the same top-down-ish camera angle; only slightly shift lookAt target.
    camera.lookAt(0 + (lateral / 100) * 1.1 * eased, 0.2, -2 - (achieved / 260) * 6.5 * eased)
  })
  return null
}

const GolfScene: React.FC<{
  timeOfDay: 'day' | 'sunset'
  windStrength: number
  windAngle: number
  shotViz: KickTrajectoryViz | null
  shotRequest: ShotRequest | null
  animProgress: number
  isAnimating: boolean
  showKicker: boolean
  onLanding: (landing: { x: number; z: number }, request: ShotRequest) => void
}> = ({ timeOfDay, windStrength, windAngle, shotViz, shotRequest, animProgress, isAnimating, showKicker, onLanding }) => {
  const isSunset = timeOfDay === 'sunset'
  return (
    <Canvas
      shadows
      dpr={[1, 1.75]}
      gl={{ antialias: true }}
      camera={{ position: [0, 6.2, 12.5], fov: 42 }}
      className="w-full h-full rounded-2xl"
    >
      <fog attach="fog" args={[isSunset ? '#f6d4bf' : '#cde3f8', 26, 85]} />
      <ambientLight intensity={isSunset ? 0.22 : 0.28} />
      <hemisphereLight args={[isSunset ? '#ffe6d0' : '#e7f0ff', '#2f5a31', isSunset ? 0.54 : 0.62]} />
      <directionalLight
        position={isSunset ? [14, 18, 18] : [24, 28, 14]}
        intensity={isSunset ? 1.1 : 1.35}
        color={isSunset ? '#ffd3a8' : '#ffffff'}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-near={1}
        shadow-camera-far={80}
        shadow-camera-left={-28}
        shadow-camera-right={28}
        shadow-camera-top={28}
        shadow-camera-bottom={-28}
      />
      <Sky
        distance={450000}
        sunPosition={isSunset ? [8, 6, 18] : [25, 15, 6]}
        inclination={isSunset ? 0.57 : 0.49}
        azimuth={isSunset ? 0.52 : 0.25}
      />
      <Suspense fallback={null}>
        <Environment preset={isSunset ? 'sunset' : 'forest'} />
      </Suspense>
      <Physics gravity={[0, -9.81, 0]}>
        <RigidBody type="fixed">
          {/* Base course terrain */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.38, 0]} receiveShadow>
            <planeGeometry args={[80, 80, 50, 50]} />
            <meshStandardMaterial color="#2a7a3d" roughness={0.98} metalness={0.01} />
          </mesh>
          {/* Fairway strip */}
          <mesh rotation={[-Math.PI / 2, -0.06, 0]} position={[-1.6, -0.365, -2.5]} receiveShadow>
            <planeGeometry args={[22, 42]} />
            <meshStandardMaterial color="#4ca24a" roughness={0.92} />
          </mesh>
          {/* Fairway mow stripes */}
          {Array.from({ length: 10 }).map((_, i) => (
            <mesh
              key={`stripe-${i}`}
              rotation={[-Math.PI / 2, -0.06, 0]}
              position={[-1.6 + (i % 2 === 0 ? -0.05 : 0.05), -0.363, -21 + i * 4.2]}
              receiveShadow
            >
              <planeGeometry args={[22, 2.05]} />
              <meshStandardMaterial
                color={i % 2 === 0 ? '#5db95d' : '#4e9f4e'}
                roughness={0.95}
                metalness={0.01}
              />
            </mesh>
          ))}
          {/* Green around hole */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.355, -10]} receiveShadow>
            <circleGeometry args={[4.4, 42]} />
            <meshStandardMaterial color="#58b64a" roughness={0.92} />
          </mesh>
          {/* Tee box */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-2.5, -0.355, 6]} receiveShadow>
            <planeGeometry args={[3.8, 3.2]} />
            <meshStandardMaterial color="#4faa4d" roughness={0.88} />
          </mesh>
          {/* Rough strip */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[9.5, -0.37, -2]} receiveShadow>
            <planeGeometry args={[13, 38]} />
            <meshStandardMaterial color="#2a6531" roughness={1} />
          </mesh>
          {/* Water hazard */}
          <WaterHazard isSunset={isSunset} />
          {/* Sand bunker near green */}
          <mesh rotation={[-Math.PI / 2, 0.22, 0]} position={[3.4, -0.352, -8.2]} receiveShadow>
            <circleGeometry args={[1.9, 28]} />
            <meshStandardMaterial color="#dccb9a" roughness={0.98} />
          </mesh>
          {/* Front bunker */}
          <mesh rotation={[-Math.PI / 2, -0.1, 0]} position={[-2.2, -0.352, -7.4]} receiveShadow>
            <circleGeometry args={[1.45, 28]} />
            <meshStandardMaterial color="#d7c493" roughness={0.98} />
          </mesh>
          {/* Cart path */}
          <mesh rotation={[-Math.PI / 2, 0.1, 0]} position={[12, -0.349, -4]} receiveShadow>
            <planeGeometry args={[2.3, 30]} />
            <meshStandardMaterial color="#6e6a61" roughness={0.95} />
          </mesh>
        </RigidBody>

        <RigidBody type="fixed" colliders="cuboid">
          <mesh position={[0, 0.7, -10]}>
            <cylinderGeometry args={[0.02, 0.02, 1.4, 12]} />
            <meshStandardMaterial color="#e5e7eb" />
          </mesh>
          <WindFlag windStrength={windStrength} />
        </RigidBody>

        <TrainingBallPhysics shotRequest={shotRequest} windStrength={windStrength} windAngle={windAngle} onLanding={onLanding} />
      </Physics>

      {/* Rolling foreground hills */}
      <Hill x={-10} z={10} sx={1.8} sy={0.65} sz={1.2} color="#2f7f3d" />
      <Hill x={12} z={8} sx={1.6} sy={0.72} sz={1.1} color="#2d7438" />
      <Hill x={-13} z={-2} sx={1.4} sy={0.58} sz={1.3} color="#2f7a3b" />
      <Hill x={10} z={-14} sx={2} sy={0.74} sz={1.5} color="#2b6f37" />

      {/* Distant mountain silhouette */}
      <Mountain x={-26} z={-35} height={10} />
      <Mountain x={-13} z={-36} height={8.6} />
      <Mountain x={0} z={-37} height={11} />
      <Mountain x={14} z={-36} height={9.2} />
      <Mountain x={28} z={-34} height={10.4} />

      {/* Tree lines for a landscape feel */}
      <Tree x={-15} z={-18} scale={1.2} />
      <Tree x={-18} z={-10} scale={1} />
      <Tree x={-17} z={2} scale={1.15} />
      <Tree x={-16} z={12} scale={0.95} />
      <Tree x={15} z={-20} scale={1.25} />
      <Tree x={17} z={-8} scale={1} />
      <Tree x={18} z={4} scale={1.1} />
      <Tree x={14} z={14} scale={0.9} />
      <Tree x={-11} z={18} scale={1.05} />
      <Tree x={-6} z={20} scale={0.95} />
      <Tree x={6} z={19} scale={1.08} />
      <Tree x={11} z={18} scale={1} />

      {/* Green-side natural details */}
      <Rock x={4.8} z={-11.3} s={0.9} />
      <Rock x={-5.4} z={-9.8} s={0.75} />
      <Rock x={8.6} z={-6.2} s={0.7} />
      <Rock x={-9.4} z={-3.5} s={0.85} />
      <GrassClump x={-4.5} z={4.2} s={1} windStrength={windStrength} />
      <GrassClump x={-3.9} z={4.7} s={0.85} windStrength={windStrength} />
      <GrassClump x={7.2} z={1.5} s={1.1} windStrength={windStrength} />
      <GrassClump x={7.6} z={2.1} s={0.9} windStrength={windStrength} />
      <GrassClump x={3.2} z={-10.8} s={0.95} windStrength={windStrength} />
      <GrassClump x={-2.9} z={-8.7} s={0.88} windStrength={windStrength} />

      {/* Moving cloud shadows for depth and realism */}
      <CloudShadowLayer />

      {/* Player kicker (SVG overlay anchored to ball start world position) */}
      {showKicker && (
        <Html
          position={[-2.54, 0.02, 6.02]}
          transform
          occlude={false}
          style={{ pointerEvents: 'none', zIndex: 10 }}
        >
          <div
            style={{
              width: 88,
              height: 'auto',
              transform: 'translate(-60px, -170px) rotate(-4deg)',
              filter: 'drop-shadow(0 8px 12px rgba(0,0,0,0.35))',
            }}
          >
            <img
              src="/golf-golfer.svg"
              alt="Golfer"
              draggable={false}
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>
        </Html>
      )}

      {shotViz && (
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[-2.5 + (shotViz.lateral / 85) * 6, -0.33, 6 - Math.min(24, (shotViz.achieved / 260) * 24)]}
          receiveShadow
        >
          <ringGeometry args={[0.25, 0.52, 30]} />
          <meshBasicMaterial
            color={shotViz.qualityColor === 'green' ? '#22c55e' : shotViz.qualityColor === 'yellow' ? '#facc15' : '#ef4444'}
            transparent
            opacity={0.75}
          />
        </mesh>
      )}

      <ContactShadows position={[0, -0.33, 0]} opacity={0.24} scale={42} blur={2.6} far={18} />
      {shotViz && <CameraFollowRig isAnimating={isAnimating} animProgress={animProgress} achieved={shotViz.achieved} lateral={shotViz.lateral} />}

      <OrbitControls enablePan={false} minDistance={6} maxDistance={20} maxPolarAngle={Math.PI / 2.1} />
    </Canvas>
  )
}

const clubs = ['Driver', '3 Wood', '5 Iron', '7 Iron', '9 Iron', 'Pitching Wedge', 'Putter']
const lies = ['tee', 'fairway', 'rough', 'green']
const outcomes = ['fairway', 'green', 'bunker', 'rough', 'hole']

type TrajectoryType = 'high' | 'mid' | 'flat' | 'roll'
type LieType = 'tee' | 'fairway' | 'rough' | 'bunker' | 'green'

type TrainingClub = {
  id: string
  label: string
  avgDistance: number
  loft: number
  trajectory: TrajectoryType
  usage: string
  dispersion: number
  rule: string
  distanceRange: string
}

const TRAINING_CLUBS: TrainingClub[] = [
  { id: 'driver', label: 'Driver', avgDistance: 230, loft: 10, trajectory: 'flat', usage: 'Long tee shots', dispersion: 18, rule: 'Avoid opening above 15° to reduce lateral misses.', distanceRange: '200–260 m' },
  { id: 'wood3', label: '3 Wood', avgDistance: 210, loft: 15, trajectory: 'mid', usage: 'Long shots with extra control', dispersion: 14, rule: 'Great second shot option with more control than driver.', distanceRange: '185–230 m' },
  { id: 'hybrid', label: 'Hybrid', avgDistance: 175, loft: 20, trajectory: 'mid', usage: 'Recovery and medium-long distance', dispersion: 11, rule: 'Very useful from rough with easier launch.', distanceRange: '155–195 m' },
  { id: 'iron4', label: 'Iron 4', avgDistance: 165, loft: 24, trajectory: 'flat', usage: 'Long accurate approach', dispersion: 11, rule: 'Best when you need distance without too much height.', distanceRange: '150–180 m' },
  { id: 'iron5', label: 'Iron 5', avgDistance: 155, loft: 27, trajectory: 'mid', usage: 'Controlled mid-long approach', dispersion: 10, rule: 'Works best from fairway with smooth tempo.', distanceRange: '140–170 m' },
  { id: 'iron6', label: 'Iron 6', avgDistance: 145, loft: 31, trajectory: 'mid', usage: 'Mid approach', dispersion: 9, rule: 'Balanced club for carry and control.', distanceRange: '130–160 m' },
  { id: 'iron7', label: 'Iron 7', avgDistance: 135, loft: 34, trajectory: 'mid', usage: 'Standard approach', dispersion: 8, rule: 'Use this as your baseline distance reference.', distanceRange: '120–150 m' },
  { id: 'iron8', label: 'Iron 8', avgDistance: 123, loft: 38, trajectory: 'high', usage: 'Higher soft landing approach', dispersion: 7, rule: 'Helps stop the ball faster near the pin.', distanceRange: '105–138 m' },
  { id: 'iron9', label: 'Iron 9', avgDistance: 112, loft: 42, trajectory: 'high', usage: 'Short precision approach', dispersion: 7, rule: 'Prioritize centered contact over extra force.', distanceRange: '95–125 m' },
  { id: 'aw', label: 'Approach Wedge', avgDistance: 98, loft: 48, trajectory: 'high', usage: 'Short controlled approach', dispersion: 6, rule: 'Ideal for medium-short approach windows.', distanceRange: '75–110 m' },
  { id: 'sw', label: 'Sand Wedge', avgDistance: 78, loft: 56, trajectory: 'high', usage: 'Bunker exits and high short shots', dispersion: 6, rule: 'From bunker, increase power to offset sand resistance.', distanceRange: '55–90 m' },
  { id: 'lw', label: 'Lob Wedge', avgDistance: 62, loft: 60, trajectory: 'high', usage: 'High soft lobs', dispersion: 5, rule: 'Use when you need quick height and fast stop.', distanceRange: '40–75 m' },
  { id: 'putter', label: 'Putter', avgDistance: 16, loft: 3, trajectory: 'roll', usage: 'Rolling shots on green', dispersion: 2, rule: 'On green, fine direction matters more than raw power.', distanceRange: '2–25 m' },
]

const TRAINING_TARGETS = [25, 45, 70, 95, 125, 150, 180, 210]

type TrainingAttempt = {
  id: string
  clubId: string
  target: number
  clubLabel: string
  achievedForward: number
  lateral: number
  distanceError: number
  directionError: number
  lie: LieType
  rating: string
  qualityColor: 'green' | 'yellow' | 'red'
  tip: string
  isChallenge: boolean
}

type KickTrajectoryViz = {
  achieved: number
  target: number
  lateral: number
  loft: number
  qualityColor: 'green' | 'yellow' | 'red'
  power: number
  direction: number
  clubLabel: string
}

type DailyChallenge = {
  clubId: string
  lie: LieType
  target: number
}

type ShotRequest = {
  id: string
  clubLabel: string
  swipeDelta: { x: number; y: number }
  swipeDistance: number
  impulse: { x: number; y: number; z: number }
}

const CLUB_PHYSICS: Record<string, { minForward: number; maxForward: number; loftFactor: number; sideFactor: number }> = {
  driver: { minForward: 8.4, maxForward: 16.2, loftFactor: 0.28, sideFactor: 0.2 },
  wood3: { minForward: 7.8, maxForward: 14.8, loftFactor: 0.3, sideFactor: 0.2 },
  hybrid: { minForward: 6.8, maxForward: 12.8, loftFactor: 0.33, sideFactor: 0.19 },
  iron4: { minForward: 6.3, maxForward: 11.8, loftFactor: 0.34, sideFactor: 0.18 },
  iron5: { minForward: 6, maxForward: 11.2, loftFactor: 0.35, sideFactor: 0.18 },
  iron6: { minForward: 5.7, maxForward: 10.6, loftFactor: 0.36, sideFactor: 0.17 },
  iron7: { minForward: 5.3, maxForward: 9.9, loftFactor: 0.37, sideFactor: 0.17 },
  iron8: { minForward: 4.9, maxForward: 9.1, loftFactor: 0.39, sideFactor: 0.16 },
  iron9: { minForward: 4.5, maxForward: 8.4, loftFactor: 0.41, sideFactor: 0.15 },
  aw: { minForward: 3.8, maxForward: 7.3, loftFactor: 0.44, sideFactor: 0.14 },
  sw: { minForward: 3.1, maxForward: 6.4, loftFactor: 0.48, sideFactor: 0.13 },
  lw: { minForward: 2.6, maxForward: 5.6, loftFactor: 0.52, sideFactor: 0.12 },
  putter: { minForward: 1.2, maxForward: 3.8, loftFactor: 0.12, sideFactor: 0.08 },
}

type ShotSummary = {
  carry: number
  offline: number
  windCarry: number
  windLateral: number
  lieFactor: number
  contact: 'perfect' | 'good' | 'mishit'
  rating: string
}

type ShotSimulationInput = {
  club: TrainingClub
  power: number
  direction: number
  lie: LieType
  windStrength: number
  windAngle: number
  target: number
  perfectBandHit: boolean
  randomize?: boolean
}

type ShotSimulationOutput = {
  achievedForward: number
  lateral: number
  distanceError: number
  directionError: number
  qualityColor: 'green' | 'yellow' | 'red'
  ratingBand: 'excellent' | 'good' | 'improvable' | 'failed'
  contact: 'perfect' | 'good' | 'mishit'
  components: {
    baseDistance: number
    windCarryEffect: number
    windLateralEffect: number
    lieFactor: number
  }
}

const simulateShot = ({
  club,
  power,
  direction,
  lie,
  windStrength,
  windAngle,
  target,
  perfectBandHit,
  randomize = true,
}: ShotSimulationInput): ShotSimulationOutput => {
  const lieFactorMap: Record<LieType, number> = {
    tee: 1,
    fairway: 1,
    rough: 0.84,
    bunker: 0.7,
    green: club.id === 'putter' ? 1 : 0.62,
  }
  const lieFactor = lieFactorMap[lie]
  const powerFactor = club.id === 'putter' ? Math.max(0.15, power / 100) : power / 100
  const headWindPenalty = Math.max(0, Math.cos((windAngle * Math.PI) / 180)) * windStrength * 0.14
  const tailWindBoost = Math.max(0, -Math.cos((windAngle * Math.PI) / 180)) * windStrength * 0.08
  const windCarryEffect = tailWindBoost - headWindPenalty

  let contact: ShotSimulationOutput['contact'] = 'good'
  let contactFactor = 1
  if (perfectBandHit) {
    contact = 'perfect'
    contactFactor = 1.03
  } else if (power > 88 || power < 16) {
    contact = 'mishit'
    contactFactor = 0.93
  }

  const baseDistance = club.avgDistance * powerFactor * lieFactor
  const randomDistanceFactor = randomize ? 1 + (Math.random() * 2 - 1) * (contact === 'mishit' ? 0.09 : 0.05) : 1
  const achievedForward = Math.max(1, Math.round(baseDistance * contactFactor * randomDistanceFactor + windCarryEffect))
  const randomSide = randomize ? (Math.random() * 2 - 1) * (club.dispersion + (contact === 'mishit' ? 4 : 0)) : 0
  const windLateralEffect = Math.sin((windAngle * Math.PI) / 180) * windStrength * 0.45
  const lateral = Math.round(Math.tan((direction * Math.PI) / 180) * achievedForward * 0.22 + randomSide + windLateralEffect)
  const distanceError = achievedForward - target
  const directionError = lateral
  const ratio = Math.abs(distanceError) / Math.max(1, target)

  const ratingBand: ShotSimulationOutput['ratingBand'] =
    ratio <= 0.1 ? 'excellent' : ratio <= 0.25 ? 'good' : ratio <= 0.4 ? 'improvable' : 'failed'
  const qualityColor: ShotSimulationOutput['qualityColor'] =
    ratio <= 0.1 ? 'green' : ratio <= 0.25 ? 'yellow' : 'red'

  return {
    achievedForward,
    lateral,
    distanceError,
    directionError,
    qualityColor,
    ratingBand,
    contact,
    components: {
      baseDistance,
      windCarryEffect,
      windLateralEffect,
      lieFactor,
    },
  }
}

const TRAINING_I18N = {
  en: {
    title: '⛳ Golf Training',
    subtitle: 'Practice clubs, distance and control with live visual feedback.',
    backToRoom: 'Back to room',
    trainingMode: 'Training',
    roundMode: 'Room round',
    trainingTitle: 'Shot training',
    trainingDesc: 'Pick a club and adjust power, direction and lie for each shot.',
    targetDistance: 'Target distance',
    selectClub: 'Select club',
    clubPrefix: 'Club',
    viewClubCard: 'View club card',
    ballOn: 'Ball on',
    objective: 'target',
    power: 'Power',
    direction: 'Direction',
    projectedDistance: 'Projected distance',
    executeShot: 'Execute shot',
    attempts: 'Attempts',
    avgError: 'Avg error',
    sessionHistory: 'Session history',
    noShotsYet: 'No training shots yet.',
    dailyChallenge: 'Daily challenge',
    club: 'Club',
    lie: 'Lie',
    attemptsCount: 'Attempts',
    bestError: 'Best error',
    startChallenge: 'Start challenge',
    challengeActive: 'Challenge active',
    perClubAccuracy: 'Accuracy by club',
    recentEight: 'Recent (8)',
    close: 'Close',
    selectClubModal: 'Select a club',
    learningCard: 'Learning card',
    idealRange: 'Ideal range',
    trajectory: 'Trajectory',
    whenToUse: 'When to use',
    keyRule: 'Key rule',
    excellent: 'Excellent',
    good: 'Good',
    improvable: 'Needs improvement',
    failed: 'Missed',
    topView: 'Top view',
    sideView: 'Side view',
    landing: 'Landing',
    roundControls: 'Round controls',
    startRound: 'Start round',
    currentHoleShots: 'Current hole shots',
    holeResults: 'Hole results',
    noHoleShotsYet: 'No shots recorded for this hole yet.',
    noCompletedHolesYet: 'No completed holes yet.',
    targetLabel: 'Target',
    errorLabel: 'Error',
    lateralLabel: 'Lateral',
    objectiveLabel: 'target',
    landingLabel: 'landing',
    deviationLabel: 'deviation',
    tipPutter: 'With putter on green, micro-angle control is key',
    tipGeneralPrefix: 'With',
    trajectoryHigh: 'High',
    trajectoryMid: 'Mid',
    trajectoryFlat: 'Flat',
    trajectoryRoll: 'Roll',
    windLabel: 'Wind',
    dayLight: 'Day light',
    sunsetLabel: 'Sunset',
    windToday: 'Wind today',
    crossAngle: 'cross angle',
    replayLabel: 'Replay',
    chargeKickPower: 'Charge kick power (arcade throw style)',
    holdToCharge: 'hold to charge',
    releaseToKick: 'Release to kick',
    perfectTimingZone: 'Perfect timing zone',
    shotSummary: 'Shot summary',
    carryLabel: 'Carry',
    offlineLabel: 'Offline',
    windEffectLabel: 'Wind effect',
    contactLabel: 'Contact',
    ratingLabel: 'Rating',
    modeLabel: 'Mode',
    kickingSetup: 'Kicking position setup',
    loftLabel: 'Loft',
    roundLabel: 'Round',
    statusLabel: 'Status',
    currentHoleLabel: 'Current hole',
    totalStrokesLabel: 'Total strokes',
    totalPointsLabel: 'Total points',
    holesCompletedLabel: 'Holes completed',
    swingRecordShot: 'Swing (record shot)',
    completeHoleLabel: 'Complete hole',
    parCurrentHole: 'Par for current hole',
    holeLabel: 'Hole',
    strokesLabel: 'strokes',
    pointsWord: 'points',
    lieFactorLabel: 'Lie factor',
    shotSetupLabel: 'Shot setup',
    kickNowLabel: 'Kick now',
    kickLabel: 'Kick',
    swipePowerLabel: 'Swipe power',
    swipeVectorLabel: 'Swipe vector',
    swipeHintLabel: 'Swipe on the course area to shoot. Drag farther for more power, and angle your swipe for direction.',
    trainingLeaderboardLabel: 'Training leaderboard',
    noTrainingScoresYetLabel: 'No training scores yet.',
  },
  es: {
    title: '⛳ Entrenamiento de Golf',
    subtitle: 'Práctica de palos, distancia y control con feedback visual.',
    backToRoom: 'Volver a la sala',
    trainingMode: 'Entreno',
    roundMode: 'Ronda',
    trainingTitle: 'Entrenamiento de tiro',
    trainingDesc: 'Elige palo y ajusta potencia, dirección y lie para cada golpe.',
    targetDistance: 'Distancia objetivo',
    selectClub: 'Seleccionar palo',
    clubPrefix: 'Palo',
    viewClubCard: 'Ver ficha del palo',
    ballOn: 'Bola en',
    objective: 'objetivo',
    power: 'Potencia',
    direction: 'Dirección',
    projectedDistance: 'Distancia proyectada',
    executeShot: 'Ejecutar golpe',
    attempts: 'Intentos',
    avgError: 'Error medio',
    sessionHistory: 'Historial de sesión',
    noShotsYet: 'No hay tiros de entrenamiento aún.',
    dailyChallenge: 'Reto del día',
    club: 'Palo',
    lie: 'Lie',
    attemptsCount: 'Intentos',
    bestError: 'Mejor error',
    startChallenge: 'Iniciar reto',
    challengeActive: 'Reto activo',
    perClubAccuracy: 'Precisión por palo',
    recentEight: 'Últimos (8)',
    close: 'Cerrar',
    selectClubModal: 'Selecciona un palo',
    learningCard: 'Ficha de aprendizaje',
    idealRange: 'Rango ideal',
    trajectory: 'Trayectoria',
    whenToUse: 'Cuándo usarlo',
    keyRule: 'Regla clave',
    excellent: 'Excelente',
    good: 'Bueno',
    improvable: 'Mejorable',
    failed: 'Fallido',
    topView: 'Vista superior',
    sideView: 'Vista lateral',
    landing: 'Caída',
    roundControls: 'Controles de ronda',
    startRound: 'Iniciar ronda',
    currentHoleShots: 'Golpes del hoyo actual',
    holeResults: 'Resultados del hoyo',
    noHoleShotsYet: 'No hay golpes registrados para este hoyo.',
    noCompletedHolesYet: 'Aún no hay hoyos completados.',
    targetLabel: 'Objetivo',
    errorLabel: 'Error',
    lateralLabel: 'Lateral',
    objectiveLabel: 'objetivo',
    landingLabel: 'caída',
    deviationLabel: 'desvío',
    tipPutter: 'Con putter en green, el control de microángulos es clave',
    tipGeneralPrefix: 'Con',
    trajectoryHigh: 'Alta',
    trajectoryMid: 'Media',
    trajectoryFlat: 'Plana',
    trajectoryRoll: 'Rodada',
    windLabel: 'Viento',
    dayLight: 'Día',
    sunsetLabel: 'Atardecer',
    windToday: 'Viento de hoy',
    crossAngle: 'ángulo cruzado',
    replayLabel: 'Repetir',
    chargeKickPower: 'Cargar potencia (estilo arcade)',
    holdToCharge: 'mantener para cargar',
    releaseToKick: 'Suelta para golpear',
    perfectTimingZone: 'Zona de timing perfecto',
    shotSummary: 'Resumen del golpe',
    carryLabel: 'Distancia',
    offlineLabel: 'Desvío',
    windEffectLabel: 'Efecto del viento',
    contactLabel: 'Contacto',
    ratingLabel: 'Valoración',
    modeLabel: 'Modo',
    kickingSetup: 'Configuración de golpe',
    loftLabel: 'Loft',
    roundLabel: 'Ronda',
    statusLabel: 'Estado',
    currentHoleLabel: 'Hoyo actual',
    totalStrokesLabel: 'Golpes totales',
    totalPointsLabel: 'Puntos totales',
    holesCompletedLabel: 'Hoyos completados',
    swingRecordShot: 'Golpear (registrar)',
    completeHoleLabel: 'Completar hoyo',
    parCurrentHole: 'Par del hoyo actual',
    holeLabel: 'Hoyo',
    strokesLabel: 'golpes',
    pointsWord: 'puntos',
    lieFactorLabel: 'Factor de lie',
    shotSetupLabel: 'Configuración del tiro',
    kickNowLabel: 'Golpear ahora',
    kickLabel: 'Golpear',
    swipePowerLabel: 'Potencia del deslizamiento',
    swipeVectorLabel: 'Vector del deslizamiento',
    swipeHintLabel: 'Desliza sobre el campo para golpear. Arrastra más para más potencia y ajusta el ángulo para la dirección.',
    trainingLeaderboardLabel: 'Clasificación de entrenamiento',
    noTrainingScoresYetLabel: 'Aún no hay puntuaciones de entrenamiento.',
  },
  fr: {
    title: '⛳ Entraînement de Golf',
    subtitle: 'Travaillez clubs, distance et contrôle avec retour visuel en direct.',
    backToRoom: 'Retour à la salle',
    trainingMode: 'Entraînement',
    roundMode: 'Manche',
    trainingTitle: 'Entraînement de tir',
    trainingDesc: 'Choisissez un club et ajustez puissance, direction et lie à chaque tir.',
    targetDistance: 'Distance cible',
    selectClub: 'Sélectionner un club',
    clubPrefix: 'Club',
    viewClubCard: 'Voir la fiche du club',
    ballOn: 'Balle sur',
    objective: 'objectif',
    power: 'Puissance',
    direction: 'Direction',
    projectedDistance: 'Distance projetée',
    executeShot: 'Exécuter le tir',
    attempts: 'Tentatives',
    avgError: 'Erreur moyenne',
    sessionHistory: 'Historique de session',
    noShotsYet: 'Aucun tir d’entraînement pour le moment.',
    dailyChallenge: 'Défi du jour',
    club: 'Club',
    lie: 'Lie',
    attemptsCount: 'Tentatives',
    bestError: 'Meilleure erreur',
    startChallenge: 'Démarrer le défi',
    challengeActive: 'Défi actif',
    perClubAccuracy: 'Précision par club',
    recentEight: 'Derniers (8)',
    close: 'Fermer',
    selectClubModal: 'Sélectionnez un club',
    learningCard: 'Fiche d’apprentissage',
    idealRange: 'Plage idéale',
    trajectory: 'Trajectoire',
    whenToUse: 'Quand l’utiliser',
    keyRule: 'Règle clé',
    excellent: 'Excellent',
    good: 'Bon',
    improvable: 'À améliorer',
    failed: 'Raté',
    topView: 'Vue du dessus',
    sideView: 'Vue latérale',
    landing: 'Atterrissage',
    roundControls: 'Contrôles de manche',
    startRound: 'Démarrer la manche',
    currentHoleShots: 'Coups du trou en cours',
    holeResults: 'Résultats du trou',
    noHoleShotsYet: 'Aucun coup enregistré pour ce trou.',
    noCompletedHolesYet: 'Aucun trou terminé pour le moment.',
    targetLabel: 'Cible',
    errorLabel: 'Erreur',
    lateralLabel: 'Latéral',
    objectiveLabel: 'objectif',
    landingLabel: 'retombée',
    deviationLabel: 'écart',
    tipPutter: 'Au putter sur le green, le micro-angle est essentiel',
    tipGeneralPrefix: 'Avec',
    trajectoryHigh: 'Haute', trajectoryMid: 'Moyenne', trajectoryFlat: 'Plate', trajectoryRoll: 'Roulée',
    windLabel: 'Vent',
    dayLight: 'Jour',
    sunsetLabel: 'Coucher du soleil',
    windToday: 'Vent du jour',
    crossAngle: 'angle croisé',
    replayLabel: 'Rejouer',
    chargeKickPower: 'Chargement de puissance (style arcade)',
    holdToCharge: 'maintenir pour charger',
    releaseToKick: 'Relâcher pour tirer',
    perfectTimingZone: 'Zone de timing parfait',
    shotSummary: 'Résumé du tir',
    carryLabel: 'Portée',
    offlineLabel: 'Déviation',
    windEffectLabel: 'Effet du vent',
    contactLabel: 'Contact',
    ratingLabel: 'Évaluation',
    modeLabel: 'Mode',
    kickingSetup: 'Configuration du tir',
    loftLabel: 'Loft',
    roundLabel: 'Manche',
    statusLabel: 'Statut',
    currentHoleLabel: 'Trou actuel',
    totalStrokesLabel: 'Coups totaux',
    totalPointsLabel: 'Points totaux',
    holesCompletedLabel: 'Trous complétés',
    swingRecordShot: 'Swing (enregistrer coup)',
    completeHoleLabel: 'Terminer le trou',
    parCurrentHole: 'Par du trou actuel',
    holeLabel: 'Trou',
    strokesLabel: 'coups',
    pointsWord: 'points',
    lieFactorLabel: 'Facteur de lie',
    shotSetupLabel: 'Configuration du tir',
    kickNowLabel: 'Tirer maintenant',
    kickLabel: 'Tirer',
    swipePowerLabel: 'Puissance du glissement',
    swipeVectorLabel: 'Vecteur du glissement',
    swipeHintLabel: 'Glissez sur le parcours pour tirer. Glissez plus loin pour plus de puissance et ajustez l’angle pour la direction.',
    trainingLeaderboardLabel: 'Classement entraînement',
    noTrainingScoresYetLabel: 'Aucun score d’entraînement pour le moment.',
  },
  de: {
    title: '⛳ Golf-Training', subtitle: 'Trainiere Schläger, Distanz und Kontrolle mit Live-Feedback.',
    backToRoom: 'Zurück zum Raum', trainingMode: 'Training', roundMode: 'Runde', trainingTitle: 'Schlagtraining',
    trainingDesc: 'Wähle einen Schläger und passe Kraft, Richtung und Lie an.', targetDistance: 'Zieldistanz',
    selectClub: 'Schläger wählen', clubPrefix: 'Schläger', viewClubCard: 'Schlägerkarte anzeigen', ballOn: 'Ball auf',
    objective: 'Ziel', power: 'Kraft', direction: 'Richtung', projectedDistance: 'Prognostizierte Distanz',
    executeShot: 'Schlag ausführen', attempts: 'Versuche', avgError: 'Ø Fehler', sessionHistory: 'Sitzungsverlauf',
    noShotsYet: 'Noch keine Trainingsschläge.', dailyChallenge: 'Tages-Challenge', club: 'Schläger', lie: 'Lie',
    attemptsCount: 'Versuche', bestError: 'Bester Fehler', startChallenge: 'Challenge starten', challengeActive: 'Challenge aktiv',
    perClubAccuracy: 'Genauigkeit pro Schläger', recentEight: 'Letzte (8)', close: 'Schließen', selectClubModal: 'Schläger wählen',
    learningCard: 'Lernkarte', idealRange: 'Idealer Bereich', trajectory: 'Flugbahn', whenToUse: 'Wann verwenden',
    keyRule: 'Wichtige Regel', excellent: 'Exzellent', good: 'Gut', improvable: 'Verbesserbar', failed: 'Fehlgeschlagen',
    topView: 'Draufsicht', sideView: 'Seitenansicht', landing: 'Landung', roundControls: 'Rundensteuerung',
    startRound: 'Runde starten', currentHoleShots: 'Schläge am aktuellen Loch', holeResults: 'Lochergebnisse',
    noHoleShotsYet: 'Noch keine Schläge für dieses Loch.', noCompletedHolesYet: 'Noch keine abgeschlossenen Löcher.',
    targetLabel: 'Ziel', errorLabel: 'Fehler', lateralLabel: 'Seitlich', objectiveLabel: 'Ziel', landingLabel: 'Landung',
    deviationLabel: 'Abweichung', tipPutter: 'Beim Putter auf dem Grün ist Mikrowinkel-Kontrolle entscheidend', tipGeneralPrefix: 'Mit',
    trajectoryHigh: 'Hoch', trajectoryMid: 'Mittel', trajectoryFlat: 'Flach', trajectoryRoll: 'Rollen',
    windLabel: 'Wind',
    dayLight: 'Tag',
    sunsetLabel: 'Sonnenuntergang',
    windToday: 'Heutiger Wind',
    crossAngle: 'Querwinkel',
    replayLabel: 'Wiederholen',
    chargeKickPower: 'Schlagkraft laden (Arcade-Stil)',
    holdToCharge: 'halten zum Laden',
    releaseToKick: 'Loslassen zum Schlag',
    perfectTimingZone: 'Perfekte Timing-Zone',
    shotSummary: 'Schussübersicht',
    carryLabel: 'Carry',
    offlineLabel: 'Abweichung',
    windEffectLabel: 'Windeffekt',
    contactLabel: 'Kontakt',
    ratingLabel: 'Bewertung',
    modeLabel: 'Modus',
    kickingSetup: 'Schlag-Setup',
    loftLabel: 'Loft',
    roundLabel: 'Runde',
    statusLabel: 'Status',
    currentHoleLabel: 'Aktuelles Loch',
    totalStrokesLabel: 'Gesamtschläge',
    totalPointsLabel: 'Gesamtpunkte',
    holesCompletedLabel: 'Abgeschlossene Löcher',
    swingRecordShot: 'Schwung (Schlag speichern)',
    completeHoleLabel: 'Loch abschließen',
    parCurrentHole: 'Par für aktuelles Loch',
    holeLabel: 'Loch',
    strokesLabel: 'Schläge',
    pointsWord: 'Punkte',
    lieFactorLabel: 'Lie-Faktor',
    shotSetupLabel: 'Schuss-Setup',
    kickNowLabel: 'Jetzt schlagen',
    kickLabel: 'Schlagen',
    swipePowerLabel: 'Wischkraft',
    swipeVectorLabel: 'Wischvektor',
    swipeHintLabel: 'Wische auf dem Platz, um zu schlagen. Weiter wischen für mehr Kraft, Winkel für die Richtung anpassen.',
    trainingLeaderboardLabel: 'Trainings-Rangliste',
    noTrainingScoresYetLabel: 'Noch keine Trainingspunkte vorhanden.',
  },
  it: {
    title: '⛳ Allenamento Golf', subtitle: 'Allena mazze, distanza e controllo con feedback visivo.',
    backToRoom: 'Torna alla stanza', trainingMode: 'Allenamento', roundMode: 'Round', trainingTitle: 'Allenamento colpo',
    trainingDesc: 'Scegli una mazza e regola potenza, direzione e lie.', targetDistance: 'Distanza obiettivo',
    selectClub: 'Seleziona mazza', clubPrefix: 'Mazza', viewClubCard: 'Vedi scheda mazza', ballOn: 'Palla su',
    objective: 'obiettivo', power: 'Potenza', direction: 'Direzione', projectedDistance: 'Distanza prevista',
    executeShot: 'Esegui colpo', attempts: 'Tentativi', avgError: 'Errore medio', sessionHistory: 'Storico sessione',
    noShotsYet: 'Nessun colpo di allenamento.', dailyChallenge: 'Sfida del giorno', club: 'Mazza', lie: 'Lie',
    attemptsCount: 'Tentativi', bestError: 'Miglior errore', startChallenge: 'Avvia sfida', challengeActive: 'Sfida attiva',
    perClubAccuracy: 'Precisione per mazza', recentEight: 'Ultimi (8)', close: 'Chiudi', selectClubModal: 'Seleziona una mazza',
    learningCard: 'Scheda apprendimento', idealRange: 'Range ideale', trajectory: 'Traiettoria', whenToUse: 'Quando usarla',
    keyRule: 'Regola chiave', excellent: 'Eccellente', good: 'Buono', improvable: 'Migliorabile', failed: 'Fallito',
    topView: 'Vista dall’alto', sideView: 'Vista laterale', landing: 'Atterraggio', roundControls: 'Controlli round',
    startRound: 'Inizia round', currentHoleShots: 'Colpi buca corrente', holeResults: 'Risultati buca',
    noHoleShotsYet: 'Nessun colpo registrato per questa buca.', noCompletedHolesYet: 'Nessuna buca completata.',
    targetLabel: 'Target', errorLabel: 'Errore', lateralLabel: 'Laterale', objectiveLabel: 'obiettivo', landingLabel: 'atterraggio',
    deviationLabel: 'deviazione', tipPutter: 'Con il putter sul green, il micro-angolo è fondamentale', tipGeneralPrefix: 'Con',
    trajectoryHigh: 'Alta', trajectoryMid: 'Media', trajectoryFlat: 'Piatta', trajectoryRoll: 'Rotolata',
    windLabel: 'Vento',
    dayLight: 'Giorno',
    sunsetLabel: 'Tramonto',
    windToday: 'Vento di oggi',
    crossAngle: 'angolo trasversale',
    replayLabel: 'Rigioca',
    chargeKickPower: 'Carica potenza tiro (stile arcade)',
    holdToCharge: 'tieni premuto per caricare',
    releaseToKick: 'Rilascia per tirare',
    perfectTimingZone: 'Zona timing perfetto',
    shotSummary: 'Riepilogo tiro',
    carryLabel: 'Distanza',
    offlineLabel: 'Fuori linea',
    windEffectLabel: 'Effetto vento',
    contactLabel: 'Contatto',
    ratingLabel: 'Valutazione',
    modeLabel: 'Modalità',
    kickingSetup: 'Configurazione tiro',
    loftLabel: 'Loft',
    roundLabel: 'Round',
    statusLabel: 'Stato',
    currentHoleLabel: 'Buca corrente',
    totalStrokesLabel: 'Colpi totali',
    totalPointsLabel: 'Punti totali',
    holesCompletedLabel: 'Buche completate',
    swingRecordShot: 'Swing (registra colpo)',
    completeHoleLabel: 'Completa buca',
    parCurrentHole: 'Par della buca corrente',
    holeLabel: 'Buca',
    strokesLabel: 'colpi',
    pointsWord: 'punti',
    lieFactorLabel: 'Fattore lie',
    shotSetupLabel: 'Configurazione tiro',
    kickNowLabel: 'Colpisci ora',
    kickLabel: 'Colpisci',
    swipePowerLabel: 'Potenza swipe',
    swipeVectorLabel: 'Vettore swipe',
    swipeHintLabel: 'Scorri sul campo per tirare. Scorri di più per più potenza e regola l’angolo per la direzione.',
    trainingLeaderboardLabel: 'Classifica allenamento',
    noTrainingScoresYetLabel: 'Nessun punteggio allenamento per ora.',
  },
  pt: {
    title: '⛳ Treino de Golfe', subtitle: 'Treine tacos, distância e controle com feedback visual.',
    backToRoom: 'Voltar à sala', trainingMode: 'Treino', roundMode: 'Rodada', trainingTitle: 'Treino de tacada',
    trainingDesc: 'Escolha um taco e ajuste potência, direção e lie.', targetDistance: 'Distância alvo',
    selectClub: 'Selecionar taco', clubPrefix: 'Taco', viewClubCard: 'Ver ficha do taco', ballOn: 'Bola no',
    objective: 'objetivo', power: 'Potência', direction: 'Direção', projectedDistance: 'Distância projetada',
    executeShot: 'Executar tacada', attempts: 'Tentativas', avgError: 'Erro médio', sessionHistory: 'Histórico da sessão',
    noShotsYet: 'Ainda sem tacadas de treino.', dailyChallenge: 'Desafio do dia', club: 'Taco', lie: 'Lie',
    attemptsCount: 'Tentativas', bestError: 'Melhor erro', startChallenge: 'Iniciar desafio', challengeActive: 'Desafio ativo',
    perClubAccuracy: 'Precisão por taco', recentEight: 'Últimas (8)', close: 'Fechar', selectClubModal: 'Selecione um taco',
    learningCard: 'Ficha de aprendizagem', idealRange: 'Faixa ideal', trajectory: 'Trajetória', whenToUse: 'Quando usar',
    keyRule: 'Regra-chave', excellent: 'Excelente', good: 'Bom', improvable: 'A melhorar', failed: 'Falhou',
    topView: 'Vista superior', sideView: 'Vista lateral', landing: 'Queda', roundControls: 'Controles da rodada',
    startRound: 'Iniciar rodada', currentHoleShots: 'Tacadas do buraco atual', holeResults: 'Resultados do buraco',
    noHoleShotsYet: 'Sem tacadas registradas neste buraco.', noCompletedHolesYet: 'Ainda sem buracos concluídos.',
    targetLabel: 'Alvo', errorLabel: 'Erro', lateralLabel: 'Lateral', objectiveLabel: 'objetivo', landingLabel: 'queda',
    deviationLabel: 'desvio', tipPutter: 'Com putter no green, microângulo é essencial', tipGeneralPrefix: 'Com',
    trajectoryHigh: 'Alta', trajectoryMid: 'Média', trajectoryFlat: 'Plana', trajectoryRoll: 'Rolada',
    windLabel: 'Vento',
    dayLight: 'Dia',
    sunsetLabel: 'Pôr do sol',
    windToday: 'Vento de hoje',
    crossAngle: 'ângulo cruzado',
    replayLabel: 'Repetir',
    chargeKickPower: 'Carregar potência (estilo arcade)',
    holdToCharge: 'segure para carregar',
    releaseToKick: 'Solte para chutar',
    perfectTimingZone: 'Zona de timing perfeito',
    shotSummary: 'Resumo da tacada',
    carryLabel: 'Distância',
    offlineLabel: 'Desvio',
    windEffectLabel: 'Efeito do vento',
    contactLabel: 'Contato',
    ratingLabel: 'Avaliação',
    modeLabel: 'Modo',
    kickingSetup: 'Configuração da tacada',
    loftLabel: 'Loft',
    roundLabel: 'Rodada',
    statusLabel: 'Status',
    currentHoleLabel: 'Buraco atual',
    totalStrokesLabel: 'Tacadas totais',
    totalPointsLabel: 'Pontos totais',
    holesCompletedLabel: 'Buracos concluídos',
    swingRecordShot: 'Swing (registrar tacada)',
    completeHoleLabel: 'Completar buraco',
    parCurrentHole: 'Par do buraco atual',
    holeLabel: 'Buraco',
    strokesLabel: 'tacadas',
    pointsWord: 'pontos',
    lieFactorLabel: 'Fator de lie',
    shotSetupLabel: 'Configuração da tacada',
    kickNowLabel: 'Chutar agora',
    kickLabel: 'Chutar',
    swipePowerLabel: 'Potência do swipe',
    swipeVectorLabel: 'Vetor do swipe',
    swipeHintLabel: 'Deslize no campo para chutar. Arraste mais para aumentar a potência e ajuste o ângulo para a direção.',
    trainingLeaderboardLabel: 'Ranking de treino',
    noTrainingScoresYetLabel: 'Ainda não há pontuações de treino.',
  },
  ru: {
    title: '⛳ Тренировка по гольфу', subtitle: 'Тренируйте клюшки, дистанцию и контроль с визуальным фидбеком.',
    backToRoom: 'Назад в комнату', trainingMode: 'Тренировка', roundMode: 'Раунд', trainingTitle: 'Тренировка удара',
    trainingDesc: 'Выберите клюшку и настройте силу, направление и lie.', targetDistance: 'Целевая дистанция',
    selectClub: 'Выбрать клюшку', clubPrefix: 'Клюшка', viewClubCard: 'Показать карточку клюшки', ballOn: 'Мяч на',
    objective: 'цель', power: 'Сила', direction: 'Направление', projectedDistance: 'Прогноз дистанции',
    executeShot: 'Выполнить удар', attempts: 'Попытки', avgError: 'Средняя ошибка', sessionHistory: 'История сессии',
    noShotsYet: 'Пока нет тренировочных ударов.', dailyChallenge: 'Челлендж дня', club: 'Клюшка', lie: 'Lie',
    attemptsCount: 'Попытки', bestError: 'Лучшая ошибка', startChallenge: 'Начать челлендж', challengeActive: 'Челлендж активен',
    perClubAccuracy: 'Точность по клюшкам', recentEight: 'Последние (8)', close: 'Закрыть', selectClubModal: 'Выберите клюшку',
    learningCard: 'Обучающая карточка', idealRange: 'Идеальный диапазон', trajectory: 'Траектория', whenToUse: 'Когда использовать',
    keyRule: 'Ключевое правило', excellent: 'Отлично', good: 'Хорошо', improvable: 'Можно улучшить', failed: 'Промах',
    topView: 'Вид сверху', sideView: 'Вид сбоку', landing: 'Приземление', roundControls: 'Управление раундом',
    startRound: 'Начать раунд', currentHoleShots: 'Удары текущей лунки', holeResults: 'Результаты лунки',
    noHoleShotsYet: 'Для этой лунки пока нет ударов.', noCompletedHolesYet: 'Пока нет завершенных лунок.',
    targetLabel: 'Цель', errorLabel: 'Ошибка', lateralLabel: 'Боковое', objectiveLabel: 'цель', landingLabel: 'приземление',
    deviationLabel: 'отклонение', tipPutter: 'С паттером на грине критичен микроугол', tipGeneralPrefix: 'С',
    trajectoryHigh: 'Высокая', trajectoryMid: 'Средняя', trajectoryFlat: 'Плоская', trajectoryRoll: 'Катящаяся',
    windLabel: 'Ветер',
    dayLight: 'День',
    sunsetLabel: 'Закат',
    windToday: 'Ветер сегодня',
    crossAngle: 'угол сноса',
    replayLabel: 'Повтор',
    chargeKickPower: 'Заряд силы удара (аркадный стиль)',
    holdToCharge: 'удерживайте для зарядки',
    releaseToKick: 'Отпустите для удара',
    perfectTimingZone: 'Зона идеального тайминга',
    shotSummary: 'Сводка удара',
    carryLabel: 'Дальность',
    offlineLabel: 'Отклонение',
    windEffectLabel: 'Эффект ветра',
    contactLabel: 'Контакт',
    ratingLabel: 'Оценка',
    modeLabel: 'Режим',
    kickingSetup: 'Настройка удара',
    loftLabel: 'Лофт',
    roundLabel: 'Раунд',
    statusLabel: 'Статус',
    currentHoleLabel: 'Текущая лунка',
    totalStrokesLabel: 'Всего ударов',
    totalPointsLabel: 'Всего очков',
    holesCompletedLabel: 'Завершено лунок',
    swingRecordShot: 'Свинг (записать удар)',
    completeHoleLabel: 'Завершить лунку',
    parCurrentHole: 'Пар текущей лунки',
    holeLabel: 'Лунка',
    strokesLabel: 'ударов',
    pointsWord: 'очков',
    lieFactorLabel: 'Коэффициент lie',
    shotSetupLabel: 'Настройка удара',
    kickNowLabel: 'Ударить сейчас',
    kickLabel: 'Удар',
    swipePowerLabel: 'Сила свайпа',
    swipeVectorLabel: 'Вектор свайпа',
    swipeHintLabel: 'Свайпните по полю для удара. Чем длиннее свайп, тем больше сила; угол задает направление.',
    trainingLeaderboardLabel: 'Тренировочный рейтинг',
    noTrainingScoresYetLabel: 'Пока нет тренировочных результатов.',
  },
  hy: {
    title: '⛳ Գոլֆի մարզում', subtitle: 'Մարզիր ակումբները, հեռավորությունն ու վերահսկումը տեսողական արձագանքով։',
    backToRoom: 'Վերադառնալ սենյակ', trainingMode: 'Մարզում', roundMode: 'Ռաունդ', trainingTitle: 'Հարվածի մարզում',
    trainingDesc: 'Ընտրիր ակումբ և կարգավորիր ուժը, ուղղությունն ու lie-ը։', targetDistance: 'Թիրախային հեռավորություն',
    selectClub: 'Ընտրել ակումբ', clubPrefix: 'Ակումբ', viewClubCard: 'Դիտել ակումբի քարտը', ballOn: 'Գնդակը՝',
    objective: 'թիրախ', power: 'Ուժ', direction: 'Ուղղություն', projectedDistance: 'Նախատեսվող հեռավորություն',
    executeShot: 'Կատարել հարված', attempts: 'Փորձեր', avgError: 'Միջին սխալ', sessionHistory: 'Սեսիայի պատմություն',
    noShotsYet: 'Դեռ մարզման հարվածներ չկան։', dailyChallenge: 'Օրվա մարտահրավեր', club: 'Ակումբ', lie: 'Lie',
    attemptsCount: 'Փորձեր', bestError: 'Լավագույն սխալ', startChallenge: 'Սկսել մարտահրավերը', challengeActive: 'Մարտահրավերը ակտիվ է',
    perClubAccuracy: 'Ճշգրտություն ըստ ակումբի', recentEight: 'Վերջին (8)', close: 'Փակել', selectClubModal: 'Ընտրիր ակումբ',
    learningCard: 'Ուսուցողական քարտ', idealRange: 'Իդեալական միջակայք', trajectory: 'Թռիչքագիծ', whenToUse: 'Երբ օգտագործել',
    keyRule: 'Գլխավոր կանոն', excellent: 'Գերազանց', good: 'Լավ', improvable: 'Կարելի է բարելավել', failed: 'Չստացվեց',
    topView: 'Վերին տեսք', sideView: 'Կողային տեսք', landing: 'Վայրէջք', roundControls: 'Ռաունդի կառավարում',
    startRound: 'Սկսել ռաունդը', currentHoleShots: 'Ընթացիկ փոսի հարվածներ', holeResults: 'Փոսի արդյունքներ',
    noHoleShotsYet: 'Այս փոսի համար հարվածներ դեռ չկան։', noCompletedHolesYet: 'Ավարտված փոսեր դեռ չկան։',
    targetLabel: 'Թիրախ', errorLabel: 'Սխալ', lateralLabel: 'Կողային', objectiveLabel: 'թիրախ', landingLabel: 'վայրէջք',
    deviationLabel: 'շեղում', tipPutter: 'Putter-ով green-ում մանր անկյան վերահսկումը կարևոր է', tipGeneralPrefix: 'Օգտագործելով',
    trajectoryHigh: 'Բարձր', trajectoryMid: 'Միջին', trajectoryFlat: 'Հարթ', trajectoryRoll: 'Գլորում',
    windLabel: 'Քամի',
    dayLight: 'Ցերեկ',
    sunsetLabel: 'Մայրամուտ',
    windToday: 'Այսօրվա քամի',
    crossAngle: 'կողմնային անկյուն',
    replayLabel: 'Կրկնել',
    chargeKickPower: 'Հարվածի ուժի լիցքավորում (arcade)',
    holdToCharge: 'պահել՝ լիցքավորելու համար',
    releaseToKick: 'Թողնել՝ հարվածելու համար',
    perfectTimingZone: 'Կատարյալ timing գոտի',
    shotSummary: 'Հարվածի ամփոփում',
    carryLabel: 'Հեռավորություն',
    offlineLabel: 'Շեղում',
    windEffectLabel: 'Քամու ազդեցություն',
    contactLabel: 'Կոնտակտ',
    ratingLabel: 'Գնահատական',
    modeLabel: 'Ռեժիմ',
    kickingSetup: 'Հարվածի կարգավորում',
    loftLabel: 'Loft',
    roundLabel: 'Ռաունդ',
    statusLabel: 'Կարգավիճակ',
    currentHoleLabel: 'Ընթացիկ փոս',
    totalStrokesLabel: 'Ընդհանուր հարվածներ',
    totalPointsLabel: 'Ընդհանուր միավորներ',
    holesCompletedLabel: 'Ավարտված փոսեր',
    swingRecordShot: 'Swing (գրանցել հարվածը)',
    completeHoleLabel: 'Ավարտել փոսը',
    parCurrentHole: 'Ընթացիկ փոսի par',
    holeLabel: 'Փոս',
    strokesLabel: 'հարված',
    pointsWord: 'միավոր',
    lieFactorLabel: 'Lie գործակից',
    shotSetupLabel: 'Հարվածի կարգավորում',
    kickNowLabel: 'Հարվածել հիմա',
    kickLabel: 'Հարվածել',
    swipePowerLabel: 'Swipe ուժ',
    swipeVectorLabel: 'Swipe վեկտոր',
    swipeHintLabel: 'Swipe արա դաշտի վրա՝ հարվածելու համար։ Ավելի երկար swipe-ը ավելի մեծ ուժ է տալիս, անկյունը՝ ուղղությունը։',
    trainingLeaderboardLabel: 'Մարզման վարկանիշ',
    noTrainingScoresYetLabel: 'Մարզման միավորներ դեռ չկան։',
  },
} as const

const GolfPlay: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const { locale } = useTranslation()
  const lang = (locale as keyof typeof TRAINING_I18N)
  const ui = { ...TRAINING_I18N.en, ...(TRAINING_I18N[lang] || {}) }
  const queryClient = useQueryClient()
  const [pendingHolePar, setPendingHolePar] = useState(4)
  const [holeFeedback, setHoleFeedback] = useState<string>('')
  const [mode, setMode] = useState<'training' | 'round'>('training')
  const [showClubModal, setShowClubModal] = useState(false)
  const [showShotSetupModal, setShowShotSetupModal] = useState(false)
  const [selectedTarget, setSelectedTarget] = useState<number>(TRAINING_TARGETS[3])
  const [selectedClub, setSelectedClub] = useState<TrainingClub | null>(null)
  const [kickPower, setKickPower] = useState<number>(70)
  const [kickLie, setKickLie] = useState<LieType>('tee')
  const [kickDirection, setKickDirection] = useState<number>(0)
  const [trainingAttempts, setTrainingAttempts] = useState<TrainingAttempt[]>([])
  const [trainingFeedback, setTrainingFeedback] = useState<string>('')
  const [lastKickViz, setLastKickViz] = useState<KickTrajectoryViz | null>(null)
  const [animProgress, setAnimProgress] = useState(0)
  const [isShotAnimating, setIsShotAnimating] = useState(false)
  const [showClubInfoCard, setShowClubInfoCard] = useState(false)
  const [seenClubCards, setSeenClubCards] = useState<Record<string, boolean>>({})
  const [dailyChallenge, setDailyChallenge] = useState<DailyChallenge | null>(null)
  const [challengeAttemptsUsed, setChallengeAttemptsUsed] = useState(0)
  const [challengeBestError, setChallengeBestError] = useState<number | null>(null)
  const [isChallengeMode, setIsChallengeMode] = useState(false)
  const [timeOfDay, setTimeOfDay] = useState<'day' | 'sunset'>('day')
  const windStrength = useGolfWindStore((s) => s.speed)
  const windAngle = useGolfWindStore((s) => s.directionDeg)
  const setWind = useGolfWindStore((s) => s.setWind)
  const [lastShotSummary, setLastShotSummary] = useState<ShotSummary | null>(null)
  const [shotRequest, setShotRequest] = useState<ShotRequest | null>(null)
  const [swipeStart, setSwipeStart] = useState<{ x: number; y: number } | null>(null)
  const [swipeCurrent, setSwipeCurrent] = useState<{ x: number; y: number } | null>(null)
  const [isSwiping, setIsSwiping] = useState(false)

  const { data: activeRound } = useQuery({
    queryKey: ['golf-active-round', roomId],
    queryFn: () => golfPlayService.getActiveRound(roomId!),
    enabled: !!roomId,
  })

  const roundId = activeRound?.id
  const { data: trainingSession } = useQuery({
    queryKey: ['golf-training-session'],
    queryFn: () => golfTrainingService.createSession(),
    staleTime: Infinity,
  })
  const { data: trainingLeaderboard } = useQuery({
    queryKey: ['golf-training-leaderboard'],
    queryFn: () => golfTrainingService.getLeaderboard(10),
    staleTime: 15000,
    refetchInterval: 15000,
  })
  const { data: roundDetails } = useQuery({
    queryKey: ['golf-round-details', roomId, roundId],
    queryFn: () => golfPlayService.getRoundDetails(roomId!, roundId!),
    enabled: !!roomId && !!roundId,
    refetchInterval: 3000,
  })

  const currentHole = activeRound?.currentHole ?? 1
  const currentWind = useMemo(
    () => trainingSession?.windSeed?.find((item) => item.hole === currentHole) || null,
    [trainingSession, currentHole],
  )

  useEffect(() => {
    if (!currentWind) return
    setWind(currentWind.speed, currentWind.directionDeg)
  }, [currentWind, setWind])
  const currentHoleShots = useMemo(
    () => (roundDetails?.shots || []).filter((s) => s.holeNumber === currentHole),
    [roundDetails?.shots, currentHole],
  )

  const startRoundMutation = useMutation({
    mutationFn: () => golfPlayService.startRound(roomId!, 1),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['golf-active-round', roomId] })
    },
  })

  const swingMutation = useMutation({
    mutationFn: (round: GolfPlayerRound) => {
      const randomDistance = Math.round(12 + Math.random() * 210)
      const randomClub = clubs[Math.floor(Math.random() * clubs.length)]
      const randomLie = lies[Math.floor(Math.random() * lies.length)]
      const randomOutcome = outcomes[Math.floor(Math.random() * outcomes.length)]
      return golfPlayService.recordShot(roomId!, round.id, {
        holeNumber: round.currentHole,
        club: randomClub,
        distanceMeters: randomDistance,
        lie: randomLie,
        outcome: randomOutcome,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['golf-round-details', roomId, roundId] })
    },
  })

  const completeHoleMutation = useMutation({
    mutationFn: (round: GolfPlayerRound) =>
      golfPlayService.completeHole(roomId!, round.id, {
        holeNumber: round.currentHole,
        par: pendingHolePar,
        strokes: Math.max(1, currentHoleShots.length),
        fairwayHit: Math.random() > 0.4,
        longestDrive: Math.random() > 0.75,
        closestToPin: Math.random() > 0.8,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['golf-active-round', roomId] })
      queryClient.invalidateQueries({ queryKey: ['golf-round-details', roomId, roundId] })
      setHoleFeedback(`${ui.holeLabel} ${result.hole.holeNumber}: +${result.hole.pointsAwarded} ${ui.pointsWord} (${result.hole.scoreType})`)
    },
  })

  const recordTrainingScoreMutation = useMutation({
    mutationFn: (payload: {
      hole: number
      club: string
      swipeDelta: { x: number; y: number }
      swipeDistance: number
      landingPosition: { x: number; z: number }
    }) => {
      if (!trainingSession?.id) return Promise.resolve(null)
      return golfTrainingService.recordScore({
        sessionId: trainingSession.id,
        ...payload,
      })
    },
  })

  const trainingAvgDelta = useMemo(() => {
    if (trainingAttempts.length === 0) return 0
    return Math.round(trainingAttempts.reduce((sum, a) => sum + Math.abs(a.distanceError), 0) / trainingAttempts.length)
  }, [trainingAttempts])

  useEffect(() => {
    if (!dailyChallenge) {
      const randomClub = TRAINING_CLUBS[Math.floor(Math.random() * TRAINING_CLUBS.length)]
      const randomLie: LieType = 'tee'
      const pool = randomClub.id === 'putter' ? [6, 10, 14, 18] : TRAINING_TARGETS
      const target = pool[Math.floor(Math.random() * pool.length)]
      setDailyChallenge({ clubId: randomClub.id, lie: randomLie, target })
    }
  }, [dailyChallenge])

  useEffect(() => {
    if (selectedClub && !seenClubCards[selectedClub.id]) {
      setShowClubInfoCard(true)
      setSeenClubCards((prev) => ({ ...prev, [selectedClub.id]: true }))
    }
  }, [selectedClub, seenClubCards])

  const attemptsByClub = useMemo(() => {
    return TRAINING_CLUBS.map((club) => {
      const clubAttempts = trainingAttempts.filter((a) => a.clubId === club.id).slice(0, 8)
      const avgError = clubAttempts.length
        ? Math.round(clubAttempts.reduce((sum, a) => sum + Math.abs(a.distanceError), 0) / clubAttempts.length)
        : null
      return { club, count: clubAttempts.length, avgError }
    })
  }, [trainingAttempts])

  const leastPracticedIds = useMemo(() => {
    const minCount = Math.min(...attemptsByClub.map((item) => item.count))
    return attemptsByClub.filter((item) => item.count === minCount).map((item) => item.club.id)
  }, [attemptsByClub])

  const projectedDistance = useMemo(() => {
    if (!selectedClub) return 0
    const projected = simulateShot({
      club: selectedClub,
      power: kickPower,
      direction: kickDirection,
      lie: kickLie,
      windStrength,
      windAngle,
      target: selectedTarget,
      perfectBandHit: false,
      randomize: false,
    })
    return projected.achievedForward
  }, [selectedClub, kickPower, kickDirection, kickLie, windAngle, windStrength, selectedTarget])

  const swipePreview = useMemo(() => {
    if (!swipeStart || !swipeCurrent || !selectedClub) return null
    const dx = swipeCurrent.x - swipeStart.x
    const dy = swipeStart.y - swipeCurrent.y
    const swipeDistance = Math.hypot(dx, dy)
    if (swipeDistance < 2) return null
    const maxSwipe = 280
    const powerRatio = Math.min(swipeDistance / maxSwipe, 1)
    const profile = CLUB_PHYSICS[selectedClub.id] || CLUB_PHYSICS.driver
    const heading = Math.atan2(dx, Math.max(1, dy)) * 0.55
    const forward = THREE.MathUtils.lerp(profile.minForward, profile.maxForward, powerRatio)
    const impulseX = Math.sin(heading) * forward * profile.sideFactor
    const impulseZ = -Math.cos(heading) * forward
    return {
      swipeDistance,
      swipeDelta: { x: dx, y: dy },
      powerPct: Math.round(powerRatio * 100),
      impulse: { x: impulseX, z: impulseZ },
    }
  }, [swipeStart, swipeCurrent, selectedClub])

  const handleBallLanding = (landing: { x: number; z: number }, request: ShotRequest) => {
    const achievedForward = Math.max(1, Math.round((6 - landing.z) * 10))
    const lateral = Math.round((landing.x + 2.5) * 10)
    const distanceError = achievedForward - selectedTarget
    const ratio = Math.abs(distanceError) / Math.max(1, selectedTarget)
    const qualityColor: 'green' | 'yellow' | 'red' = ratio <= 0.1 ? 'green' : ratio <= 0.25 ? 'yellow' : 'red'
    const rating = ratio <= 0.1 ? ui.excellent : ratio <= 0.25 ? ui.good : ratio <= 0.4 ? ui.improvable : ui.failed
    setTrainingAttempts((prev) => [
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        clubId: selectedClub?.id || 'driver',
        target: selectedTarget,
        clubLabel: request.clubLabel,
        achievedForward,
        lateral,
        distanceError,
        directionError: lateral,
        lie: kickLie,
        rating,
        qualityColor,
        tip: `${ui.tipGeneralPrefix} ${request.clubLabel}`,
        isChallenge: isChallengeMode,
      },
      ...prev,
    ].slice(0, 64))
    setLastKickViz({
      achieved: achievedForward,
      target: selectedTarget,
      lateral,
      loft: selectedClub?.loft || 10,
      qualityColor,
      power: Math.max(5, Math.min(100, Math.round(request.swipeDistance / 2.8))),
      direction: Math.round((Math.atan2(request.swipeDelta.x, Math.max(1, request.swipeDelta.y)) * 180) / Math.PI),
      clubLabel: request.clubLabel,
    })
    setTrainingFeedback(
      `${rating}: ${ui.objectiveLabel} ${selectedTarget}m | ${ui.landingLabel} ${achievedForward}m | ${ui.errorLabel.toLowerCase()} ${
        distanceError >= 0 ? '+' : ''
      }${distanceError}m`,
    )
    setLastShotSummary({
      carry: achievedForward,
      offline: lateral,
      windCarry: Math.cos((windAngle * Math.PI) / 180) * windStrength * 0.2,
      windLateral: Math.sin((windAngle * Math.PI) / 180) * windStrength * 0.2,
      lieFactor: 1,
      contact: 'good',
      rating,
    })
    setAnimProgress(1)
    setIsShotAnimating(false)
    recordTrainingScoreMutation.mutate({
      hole: currentHole,
      club: request.clubLabel,
      swipeDelta: request.swipeDelta,
      swipeDistance: request.swipeDistance,
      landingPosition: landing,
    })
  }

  const handleSwipeStart = (x: number, y: number) => {
    if (!selectedClub) return
    setIsSwiping(true)
    setSwipeStart({ x, y })
    setSwipeCurrent({ x, y })
  }

  const handleSwipeMove = (x: number, y: number) => {
    if (!isSwiping) return
    setSwipeCurrent({ x, y })
  }

  const handleSwipeEnd = () => {
    if (!selectedClub || !swipePreview) {
      setIsSwiping(false)
      setSwipeStart(null)
      setSwipeCurrent(null)
      return
    }
    const profile = CLUB_PHYSICS[selectedClub.id] || CLUB_PHYSICS.driver
    const powerNorm = Math.min(swipePreview.powerPct / 100, 1)
    const forward = THREE.MathUtils.lerp(profile.minForward, profile.maxForward, powerNorm)
    const verticalAngle = (selectedClub.loft * Math.PI) / 180
    const impulse = {
      x: swipePreview.impulse.x,
      y: Math.max(0.38, forward * Math.sin(verticalAngle) * profile.loftFactor),
      z: swipePreview.impulse.z,
    }
    setKickPower(swipePreview.powerPct)
    setShotRequest({
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      clubLabel: selectedClub.label,
      swipeDelta: swipePreview.swipeDelta,
      swipeDistance: swipePreview.swipeDistance,
      impulse,
    })
    setIsShotAnimating(true)
    setAnimProgress(0)
    setIsSwiping(false)
    setSwipeStart(null)
    setSwipeCurrent(null)
  }

  const handleKickButton = () => {
    if (!selectedClub) {
      setShowShotSetupModal(true)
      return
    }
    const profile = CLUB_PHYSICS[selectedClub.id] || CLUB_PHYSICS.driver
    const angleRad = ((kickDirection * 0.55) * Math.PI) / 180
    const syntheticSwipeDistance = Math.max(28, Math.min(280, (kickPower / 100) * 280))
    const powerNorm = Math.min(kickPower / 100, 1)
    const forward = THREE.MathUtils.lerp(profile.minForward, profile.maxForward, powerNorm)
    const verticalAngle = (selectedClub.loft * Math.PI) / 180
    const impulse = {
      x: Math.sin(angleRad) * forward * profile.sideFactor,
      y: Math.max(0.38, forward * Math.sin(verticalAngle) * profile.loftFactor),
      z: -Math.cos(angleRad) * forward,
    }
    setShotRequest({
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      clubLabel: selectedClub.label,
      swipeDelta: {
        x: Math.sin(angleRad) * syntheticSwipeDistance,
        y: Math.cos(angleRad) * syntheticSwipeDistance,
      },
      swipeDistance: syntheticSwipeDistance,
      impulse,
    })
    setIsShotAnimating(true)
    setAnimProgress(0)
  }

  useEffect(() => {
    if (!isShotAnimating) return
    const started = performance.now()
    const duration = 2600
    let raf = 0
    const tick = (t: number) => {
      const p = Math.min(1, (t - started) / duration)
      setAnimProgress(p)
      if (p < 1 && isShotAnimating) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      if (raf) cancelAnimationFrame(raf)
    }
  }, [isShotAnimating])

  if (!roomId) return null

  return (
    <div className="min-h-screen bg-background pt-16 sm:pt-20 pb-10">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="glass-card rounded-2xl p-4 sm:p-5 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-display text-display-2 font-bold text-foreground">{ui.title}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {ui.subtitle}
            </p>
          </div>
          <Link to={`/golf/${roomId}`} className="px-4 py-2 rounded-xl bg-muted/30 border border-border text-foreground font-semibold text-sm hover:bg-muted/50">
            {ui.backToRoom}
          </Link>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-5">
          <div className="relative xl:col-span-2 glass-card rounded-2xl p-3 h-[460px] sm:h-[560px] overflow-hidden">
            <GolfScene
              timeOfDay={timeOfDay}
              windStrength={windStrength}
              windAngle={windAngle}
              shotViz={lastKickViz}
              shotRequest={shotRequest}
              animProgress={animProgress}
              isAnimating={isShotAnimating}
              showKicker={
                mode === 'training' &&
                isShotAnimating &&
                // Show for the full shot animation to avoid missing the window.
                animProgress < 1
              }
              onLanding={handleBallLanding}
            />
            {mode === 'training' && (
              <div className="absolute right-3 top-3 z-20 rounded-md bg-black/55 border border-white/20 px-2 py-1 text-[11px] text-white/95 flex items-center gap-2 pointer-events-none">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-white/40">
                  <span
                    className="block w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[8px] border-b-cyan-300"
                    style={{ transform: `rotate(${windAngle}deg)` }}
                  />
                </span>
                <span>{ui.windLabel} {windStrength.toFixed(1)} kt</span>
              </div>
            )}
            {mode === 'training' && selectedClub && (
              <div
                className="absolute inset-3 z-10"
                onPointerDown={(e) => handleSwipeStart(e.clientX, e.clientY)}
                onPointerMove={(e) => handleSwipeMove(e.clientX, e.clientY)}
                onPointerUp={handleSwipeEnd}
                onPointerCancel={handleSwipeEnd}
                style={{ touchAction: 'none' }}
              />
            )}
            {mode === 'training' && (
              <div className="absolute right-3 bottom-3 z-20 flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowShotSetupModal(true)}
                  className="px-3 py-2 rounded-lg bg-white/15 border border-white/25 text-white text-xs font-semibold hover:bg-white/25"
                >
                  {ui.shotSetupLabel}
                </button>
                <button
                  type="button"
                  onClick={handleKickButton}
                  disabled={isShotAnimating}
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:opacity-90 disabled:opacity-60"
                >
                  {ui.kickLabel}
                </button>
              </div>
            )}
            {mode === 'training' && (lastKickViz || swipePreview) && (
              <div className="absolute inset-3 pointer-events-none">
                <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {(() => {
                    const achievedForOverlay = lastKickViz?.achieved || Math.round((swipePreview?.swipeDistance || 0) * 0.55)
                    const lateralForOverlay = lastKickViz?.lateral || Math.round((swipePreview?.swipeDelta.x || 0) * 0.08)
                    const targetForOverlay = lastKickViz?.target || selectedTarget
                    const maxDistance = 260
                    const normalized = Math.min(1, Math.max(0, achievedForOverlay / maxDistance))
                    const startX = 50
                    const startY = 90
                    const endX = Math.min(95, Math.max(5, 50 + (lateralForOverlay / 60) * 28))
                    const endY = Math.max(8, 90 - normalized * 72)
                    const ballX = startX + (endX - startX) * animProgress
                    const ballY = startY + (endY - startY) * animProgress
                    const zoneColor =
                      (lastKickViz?.qualityColor || 'yellow') === 'green' ? '#22c55e' : (lastKickViz?.qualityColor || 'yellow') === 'yellow' ? '#facc15' : '#ef4444'
                    const target = targetForOverlay
                    const targetNorm = Math.min(1, Math.max(0, target / maxDistance))
                    const targetEndY = Math.max(8, 90 - targetNorm * 72)
                    const scaleY = 72 / maxDistance
                    const scaleX = 28 / 60
                    const goodR = target * 0.1
                    const okR = target * 0.25
                    return (
                      <>
                        <rect x={0} y={0} width={100} height={100} fill="rgba(22,101,52,0.35)" />
                        <line x1={startX} y1={startY} x2={endX} y2={endY} fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="0.7" strokeDasharray="1.6 1.2" />

                        {/* Target rings (game readability): good + acceptable bands */}
                        <ellipse cx={startX} cy={targetEndY} rx={goodR * scaleX} ry={goodR * scaleY} fill="#22c55e" opacity="0.14" />
                        <ellipse cx={startX} cy={targetEndY} rx={okR * scaleX} ry={okR * scaleY} fill="#facc15" opacity="0.08" />

                        {Array.from({ length: 14 }).map((_, i) => {
                          const step = i / 13
                          if (step > animProgress) return null
                          const tx = startX + (endX - startX) * step
                          const ty = startY + (endY - startY) * step
                          return <circle key={`trail-top-${i}`} cx={tx} cy={ty} r="0.45" fill="rgba(255,255,255,0.65)" />
                        })}
                        <circle cx={endX} cy={endY} r="2.3" fill={zoneColor} opacity="0.2" />
                        <circle cx={endX} cy={endY} r="1.5" fill={zoneColor} stroke="rgba(255,255,255,0.9)" strokeWidth="0.35" />
                        <circle cx={ballX} cy={ballY} r="1.1" fill="#ffffff" />
                        <text x={Math.min(86, endX + 2)} y={Math.max(6, endY - 1.8)} fontSize="2.5" fill="#e5f7ea">
                          {ui.landing} {achievedForOverlay}m
                        </text>
                        <text x={3} y={8} fontSize="2.8" fill="#e5f7ea">
                          {ui.topView}
                        </text>
                      </>
                    )
                  })()}
                </svg>
                {/* side-view intentionally removed for cleaner single-view training */}
                <div className="absolute left-2 bottom-2 rounded-md bg-black/45 border border-white/15 px-2 py-1 text-[11px] text-white/90">
                  {(lastKickViz?.clubLabel || selectedClub?.label || ui.selectClub)} · {(lastKickViz?.power || swipePreview?.powerPct || 0)}% power · {(lastKickViz?.direction || 0) >= 0 ? '+' : ''}{lastKickViz?.direction || 0}°
                </div>
                <div className="absolute left-2 top-2 rounded-md bg-black/55 border border-white/20 px-2 py-1 text-[11px] text-white/95 flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-white/40">
                    <span
                      className="block w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-b-[7px] border-b-cyan-300"
                      style={{ transform: `rotate(${windAngle}deg)` }}
                    />
                  </span>
                  <span>{ui.windLabel} {windStrength.toFixed(1)} kt</span>
                  <span className="text-white/70">({windAngle >= 0 ? '+' : ''}{windAngle}°)</span>
                </div>
                <div className="absolute right-2 bottom-2 rounded-md bg-black/45 border border-white/15 px-2 py-1 text-[11px] text-white/90 flex items-center gap-2">
                  <span>{isShotAnimating ? `${Math.round(animProgress * 100)}%` : '100%'}</span>
                  <button
                    type="button"
                    onClick={() => {
                      if (!lastKickViz && !swipePreview) return
                      setAnimProgress(0)
                      setIsShotAnimating(true)
                      const start = performance.now()
                      const duration = (lastKickViz?.clubLabel || '').toLowerCase().includes('putter') ? 2200 : 2800
                      const animate = (t: number) => {
                        const p = Math.min(1, (t - start) / duration)
                        setAnimProgress(p)
                        if (p < 1) requestAnimationFrame(animate)
                        else setIsShotAnimating(false)
                      }
                      requestAnimationFrame(animate)
                    }}
                    className="pointer-events-auto rounded bg-white/15 px-2 py-0.5 hover:bg-white/25"
                  >
                    {ui.replayLabel}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div className="glass-card rounded-2xl p-4">
              <h2 className="font-semibold text-foreground mb-3">{ui.modeLabel}</h2>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setMode('training')}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                    mode === 'training'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-muted/40 border border-border text-foreground hover:bg-muted/60'
                  }`}
                >
                  {ui.trainingMode}
                </button>
                <button
                  onClick={() => setMode('round')}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                    mode === 'round'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/40 border border-border text-foreground hover:bg-muted/60'
                  }`}
                >
                  {ui.roundMode}
                </button>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  onClick={() => setTimeOfDay('day')}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                    timeOfDay === 'day'
                      ? 'bg-sky-500/80 text-white'
                      : 'bg-muted/40 border border-border text-foreground hover:bg-muted/60'
                  }`}
                >
                  {ui.dayLight}
                </button>
                <button
                  onClick={() => setTimeOfDay('sunset')}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                    timeOfDay === 'sunset'
                      ? 'bg-orange-500/80 text-white'
                      : 'bg-muted/40 border border-border text-foreground hover:bg-muted/60'
                  }`}
                >
                  {ui.sunsetLabel}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                {ui.windToday}: {windStrength} km/h · {ui.crossAngle} {windAngle >= 0 ? '+' : ''}{windAngle}°
              </p>
            </div>

            {mode === 'training' && (
              <div className="glass-card rounded-2xl p-4">
                <h2 className="font-semibold text-foreground mb-3">{ui.trainingTitle}</h2>
                <p className="text-xs text-muted-foreground mb-3">
                  {ui.trainingDesc}
                </p>
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">{ui.targetDistance}</label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {TRAINING_TARGETS.map((target) => (
                      <button
                        key={target}
                        onClick={() => setSelectedTarget(target)}
                        className={`px-2 py-1.5 rounded-md text-xs font-semibold transition-all ${
                          selectedTarget === target
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted/40 border border-border text-foreground hover:bg-muted/60'
                        }`}
                      >
                        {target}m
                      </button>
                    ))}
                  </div>

                  <div className="pt-2">
                    <button
                      onClick={() => setShowClubModal(true)}
                      className="w-full px-3 py-2 rounded-lg bg-muted/40 border border-border text-foreground font-semibold text-sm hover:bg-muted/60"
                    >
                      {selectedClub ? `${ui.clubPrefix}: ${selectedClub.label}` : ui.selectClub}
                    </button>
                    <button
                      onClick={() => setShowClubInfoCard(true)}
                      disabled={!selectedClub}
                      className="mt-2 w-full px-3 py-2 rounded-lg bg-muted/40 border border-border text-foreground font-semibold text-sm hover:bg-muted/60 disabled:opacity-50"
                    >
                      {ui.viewClubCard}
                    </button>
                  </div>

                  {selectedClub && (
                    <div className="mt-2 rounded-lg border border-border bg-muted/20 p-3">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground mb-2">
                        {ui.kickingSetup}
                      </h3>
                      <div className="space-y-2 text-xs text-muted-foreground">
                        <p>
                          {ui.ballOn} <span className="text-foreground font-medium">{kickLie}</span> · {ui.objective}{' '}
                          <span className="text-foreground font-medium">{selectedTarget}m</span>
                        </p>
                        <div>
                          <label className="block mb-1">{ui.lie}</label>
                          <div className="rounded-lg border border-border bg-background/40 px-2 py-1.5 text-[11px] text-muted-foreground font-semibold text-center">
                            tee
                          </div>
                        </div>

                        <p>
                          {ui.projectedDistance}:{' '}
                          <span className="text-foreground font-semibold">{projectedDistance}m</span>
                        </p>
                        <p>
                          {ui.swipePowerLabel}:{' '}
                          <span className="text-foreground font-semibold">{swipePreview?.powerPct || 0}%</span>
                        </p>
                        <p>
                          {ui.swipeVectorLabel}:{' '}
                          <span className="text-foreground font-semibold">
                            {swipePreview ? `${Math.round(swipePreview.swipeDelta.x)}, ${Math.round(swipePreview.swipeDelta.y)}` : '0, 0'}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-100">
                    {ui.swipeHintLabel}
                  </div>
                </div>

                {trainingFeedback && (
                  <p className="mt-3 text-xs text-emerald-300 bg-emerald-950/20 border border-emerald-500/30 rounded-lg px-2 py-1.5">
                    {trainingFeedback}
                  </p>
                )}
                {lastShotSummary && (
                  <div className="mt-2 rounded-lg border border-border/50 bg-background/50 p-2 text-[11px] text-muted-foreground">
                    <p className="text-foreground font-semibold mb-1">{ui.shotSummary}</p>
                    <p>{ui.carryLabel}: <span className="text-foreground">{lastShotSummary.carry}m</span> · {ui.offlineLabel}: <span className="text-foreground">{lastShotSummary.offline >= 0 ? '+' : ''}{lastShotSummary.offline}m</span></p>
                    <p>{ui.windEffectLabel}: <span className="text-foreground">{lastShotSummary.windCarry >= 0 ? '+' : ''}{lastShotSummary.windCarry.toFixed(1)}m</span> {ui.carryLabel.toLowerCase()} · <span className="text-foreground">{lastShotSummary.windLateral >= 0 ? '+' : ''}{lastShotSummary.windLateral.toFixed(1)}m</span> {ui.lateralLabel.toLowerCase()}</p>
                    <p>{ui.lieFactorLabel}: <span className="text-foreground">{lastShotSummary.lieFactor.toFixed(2)}x</span> · {ui.contactLabel}: <span className="text-foreground">{lastShotSummary.contact}</span> · {ui.ratingLabel}: <span className="text-foreground">{lastShotSummary.rating}</span></p>
                  </div>
                )}

                <div className="mt-3 text-xs text-muted-foreground">
                  {ui.attempts}: <span className="text-foreground font-semibold">{trainingAttempts.length}</span> ·
                  {ui.avgError}: <span className="text-foreground font-semibold"> {trainingAvgDelta}m</span>
                </div>
              </div>
            )}

            {mode === 'training' && (
              <div className="glass-card rounded-2xl p-4">
                <h2 className="font-semibold text-foreground mb-3">{ui.trainingLeaderboardLabel}</h2>
                <div className="space-y-1.5 max-h-44 overflow-y-auto text-xs">
                  {(trainingLeaderboard || []).map((row) => (
                    <div key={`lb-${row.rank}-${row.userId}`} className="rounded-lg border border-border/40 bg-muted/20 px-2 py-1.5">
                      <p className="text-foreground font-medium">#{row.rank} · {row.userId.slice(0, 8)}...</p>
                      <p className="text-muted-foreground">Best: {row.bestScore} · Avg: {row.avgScore} · Shots: {row.shots}</p>
                    </div>
                  ))}
                  {(!trainingLeaderboard || trainingLeaderboard.length === 0) && (
                    <p className="text-muted-foreground">{ui.noTrainingScoresYetLabel}</p>
                  )}
                </div>
              </div>
            )}

            {mode === 'round' && (
            <div className="glass-card rounded-2xl p-4">
              <h2 className="font-semibold text-foreground mb-3">{ui.roundControls}</h2>
              {!activeRound ? (
                <button
                  onClick={() => startRoundMutation.mutate()}
                  disabled={startRoundMutation.isPending}
                  className="w-full px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-60"
                >
                  <Play className="w-4 h-4 inline mr-1.5" />
                  {ui.startRound}
                </button>
              ) : (
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>{ui.roundLabel}: <span className="text-foreground font-medium">{activeRound.id.slice(0, 8)}...</span></p>
                  <p>{ui.statusLabel}: <span className="text-foreground font-medium">{activeRound.status}</span></p>
                  <p>{ui.currentHoleLabel}: <span className="text-foreground font-medium">{activeRound.currentHole}</span></p>
                  <p>{ui.totalStrokesLabel}: <span className="text-foreground font-medium">{activeRound.totalStrokes}</span></p>
                  <p>{ui.totalPointsLabel}: <span className="text-foreground font-medium">{activeRound.totalPoints}</span></p>
                  <p>{ui.holesCompletedLabel}: <span className="text-foreground font-medium">{activeRound.holesCompleted}/18</span></p>
                  <div className="pt-2 flex gap-2">
                    <button
                      onClick={() => swingMutation.mutate(activeRound)}
                      disabled={swingMutation.isPending}
                      className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground font-semibold text-xs hover:opacity-90 disabled:opacity-60"
                    >
                      {ui.swingRecordShot}
                    </button>
                    <button
                      onClick={() => completeHoleMutation.mutate(activeRound)}
                      disabled={completeHoleMutation.isPending}
                      className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 text-white font-semibold text-xs hover:opacity-90 disabled:opacity-60"
                    >
                      {ui.completeHoleLabel}
                    </button>
                  </div>
                  <div className="pt-1">
                    <label className="text-xs uppercase tracking-wider">{ui.parCurrentHole}</label>
                    <input
                      type="number"
                      min={3}
                      max={5}
                      value={pendingHolePar}
                      onChange={(e) => setPendingHolePar(Number(e.target.value || 4))}
                      className="mt-1 w-full px-2.5 py-1.5 rounded-lg bg-background border border-border text-foreground"
                    />
                  </div>
                  {holeFeedback && (
                    <p className="text-xs text-emerald-300 bg-emerald-950/20 border border-emerald-500/30 rounded-lg px-2 py-1.5">
                      {holeFeedback}
                    </p>
                  )}
                </div>
              )}
            </div>
            )}

            <div className="glass-card rounded-2xl p-4">
              <h2 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Flag className="w-4 h-4 text-primary" />
                {mode === 'training' ? ui.sessionHistory : ui.currentHoleShots}
              </h2>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {mode === 'training' ? (
                  <>
                    {trainingAttempts.slice(0, 20).map((a, idx) => (
                      <div key={a.id} className="rounded-lg bg-muted/30 border border-border/40 p-2 text-xs text-muted-foreground">
                        <p className="text-foreground font-medium">#{idx + 1} {a.clubLabel}</p>
                        <p>
                          {ui.targetLabel} {a.target}m · {ui.landing} {a.achievedForward}m · {ui.errorLabel} {a.distanceError >= 0 ? '+' : ''}{a.distanceError}m ·
                          {ui.lateralLabel} {a.directionError >= 0 ? '+' : ''}{a.directionError}m
                        </p>
                        <p className={a.qualityColor === 'green' ? 'text-emerald-300' : a.qualityColor === 'yellow' ? 'text-amber-200' : 'text-rose-300'}>
                          {a.rating}
                        </p>
                      </div>
                    ))}
                    {trainingAttempts.length === 0 && (
                      <p className="text-xs text-muted-foreground">{ui.noShotsYet}</p>
                    )}
                  </>
                ) : (
                  <>
                    {currentHoleShots.map((shot) => (
                      <div key={shot.id} className="rounded-lg bg-muted/30 border border-border/40 p-2 text-xs text-muted-foreground">
                        <p className="text-foreground font-medium">#{shot.shotNumber} {shot.club}</p>
                        <p>{shot.distanceMeters}m · {shot.lie} → {shot.outcome}</p>
                      </div>
                    ))}
                    {currentHoleShots.length === 0 && (
                      <p className="text-xs text-muted-foreground">{ui.noHoleShotsYet}</p>
                    )}
                  </>
                )}
              </div>
            </div>

            {mode === 'training' && (
              <div className="glass-card rounded-2xl p-4">
                <h2 className="font-semibold text-foreground mb-3">{ui.dailyChallenge}</h2>
                {dailyChallenge && (
                  <div className="text-xs text-muted-foreground space-y-2">
                    <p>
                      {ui.club}: <span className="text-foreground font-semibold">{TRAINING_CLUBS.find((c) => c.id === dailyChallenge.clubId)?.label}</span>
                    </p>
                    <p>
                      {ui.lie}: <span className="text-foreground font-semibold">{dailyChallenge.lie}</span> ·
                      {ui.objective}: <span className="text-foreground font-semibold"> {dailyChallenge.target}m</span>
                    </p>
                    <p>
                      {ui.attemptsCount}: <span className="text-foreground font-semibold">{challengeAttemptsUsed}/3</span> · {ui.bestError}:{' '}
                      <span className="text-foreground font-semibold">{challengeBestError === null ? '-' : `${challengeBestError}m`}</span>
                    </p>
                    <button
                      onClick={() => {
                        const challengeClub = TRAINING_CLUBS.find((c) => c.id === dailyChallenge.clubId)
                        if (!challengeClub) return
                        setSelectedClub(challengeClub)
                        setKickLie(dailyChallenge.lie)
                        setSelectedTarget(dailyChallenge.target)
                        setIsChallengeMode(true)
                        setChallengeAttemptsUsed(0)
                        setChallengeBestError(null)
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-amber-500/85 text-black font-semibold"
                    >
                      {isChallengeMode ? ui.challengeActive : ui.startChallenge}
                    </button>
                  </div>
                )}
              </div>
            )}

            {mode === 'training' && (
              <div className="glass-card rounded-2xl p-4">
                <h2 className="font-semibold text-foreground mb-3">{ui.perClubAccuracy}</h2>
                <div className="space-y-1.5 max-h-52 overflow-y-auto">
                  {attemptsByClub.map((item) => (
                    <div
                      key={item.club.id}
                      className={`rounded-lg p-2 text-xs border ${
                        leastPracticedIds.includes(item.club.id)
                          ? 'bg-amber-500/10 border-amber-400/40'
                          : 'bg-muted/30 border-border/40'
                      }`}
                    >
                      <p className="text-foreground font-medium">{item.club.label}</p>
                      <p className="text-muted-foreground">
                        {ui.recentEight}: {item.count} · {ui.avgError}: {item.avgError === null ? '-' : `${item.avgError}m`}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {mode === 'round' && (
            <div className="glass-card rounded-2xl p-4">
              <h2 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <CircleDot className="w-4 h-4 text-primary" />
                {ui.holeResults}
              </h2>
              <div className="space-y-1.5 max-h-52 overflow-y-auto">
                {(roundDetails?.holes || []).map((hole) => (
                  <div key={hole.id} className="rounded-lg bg-muted/30 border border-border/40 p-2 text-xs text-muted-foreground">
                    <p className="text-foreground font-medium">
                      {ui.holeLabel} {hole.holeNumber} · {hole.strokes} {ui.strokesLabel} / par {hole.par}
                    </p>
                    <p>{hole.scoreType} · +{hole.pointsAwarded} {ui.pointsWord}</p>
                  </div>
                ))}
                {(roundDetails?.holes || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">{ui.noCompletedHolesYet}</p>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Club picker modal for training kicks */}
      {showClubModal && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowClubModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label={ui.selectClubModal}
        >
          <div
            className="w-full max-w-lg rounded-2xl glass-strong border border-border p-4 sm:p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-foreground">{ui.selectClubModal}</h3>
              <button
                onClick={() => setShowClubModal(false)}
                className="px-2 py-1 rounded-md bg-muted/40 border border-border text-xs text-foreground"
              >
                {ui.close}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[55vh] overflow-y-auto pr-1">
              {TRAINING_CLUBS.map((club) => (
                <button
                  key={club.id}
                  onClick={() => {
                    setSelectedClub(club)
                    setShowClubModal(false)
                  }}
                  className={`text-left px-3 py-2 rounded-lg border text-xs transition-all ${
                    selectedClub?.id === club.id
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted/50'
                  }`}
                >
                  <ClubThumbnail clubId={club.id} clubLabel={club.label} />
                  <p className="font-semibold text-foreground">{club.label}</p>
                  <p>{club.distanceRange} · Loft {club.loft}°</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {showShotSetupModal && (
        <div
          className="fixed inset-0 z-[85] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowShotSetupModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label={ui.shotSetupLabel}
        >
          <div
            className="w-full max-w-lg rounded-2xl glass-strong border border-border p-4 sm:p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-foreground">{ui.shotSetupLabel}</h3>
              <button
                onClick={() => setShowShotSetupModal(false)}
                className="px-2 py-1 rounded-md bg-muted/40 border border-border text-xs text-foreground"
              >
                {ui.close}
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">{ui.club}</label>
                <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
                  {TRAINING_CLUBS.map((club) => (
                    <button
                      key={`setup-${club.id}`}
                      type="button"
                      onClick={() => setSelectedClub(club)}
                      className={`text-left rounded-lg border px-2 py-2 text-xs ${
                        selectedClub?.id === club.id
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border bg-muted/30 text-muted-foreground'
                      }`}
                    >
                      {club.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block mb-1 text-xs text-muted-foreground">{ui.power}: {kickPower}%</label>
                <input
                  type="range"
                  min={20}
                  max={100}
                  step={1}
                  value={kickPower}
                  onChange={(e) => setKickPower(Number(e.target.value))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-muted-foreground">{ui.direction}: {kickDirection >= 0 ? '+' : ''}{kickDirection}°</label>
                <input
                  type="range"
                  min={-45}
                  max={45}
                  step={1}
                  value={kickDirection}
                  onChange={(e) => setKickDirection(Number(e.target.value))}
                  className="w-full"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowShotSetupModal(false)
                  handleKickButton()
                }}
                disabled={!selectedClub || isShotAnimating}
                className="w-full px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
              >
                {ui.kickNowLabel}
              </button>
            </div>
          </div>
        </div>
      )}
      {showClubInfoCard && selectedClub && (
        <div
          className="fixed inset-0 z-[80] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowClubInfoCard(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Ficha del palo"
        >
          <div
            className="w-full max-w-md rounded-2xl glass-strong border border-border p-4 sm:p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-foreground">{ui.learningCard}</h3>
              <button
                onClick={() => setShowClubInfoCard(false)}
                className="px-2 py-1 rounded-md bg-muted/40 border border-border text-xs text-foreground"
              >
                {ui.close}
              </button>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><span className="text-foreground font-semibold">{selectedClub.label}</span></p>
              <p>{ui.loftLabel}: <span className="text-foreground">{selectedClub.loft}°</span></p>
              <p>{ui.idealRange}: <span className="text-foreground">{selectedClub.distanceRange}</span></p>
              <p>{ui.trajectory}: <span className="text-foreground">
                {selectedClub.trajectory === 'high'
                  ? ui.trajectoryHigh
                  : selectedClub.trajectory === 'mid'
                    ? ui.trajectoryMid
                    : selectedClub.trajectory === 'flat'
                      ? ui.trajectoryFlat
                      : ui.trajectoryRoll}
              </span></p>
              <p>{ui.whenToUse}: <span className="text-foreground">{selectedClub.usage}</span></p>
              <p>{ui.keyRule}: <span className="text-foreground">{selectedClub.rule}</span></p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GolfPlay

